// API Service - Connects frontend to backend
// Version 2.3 - Capacitor production support
console.log('%c[API SERVICE] v2.3 Initialized', 'color: blue; font-weight: bold;');
const host = window.location.hostname;
const port = window.location.port;
const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();
// Resolve API base with robust local-dev detection. Prefer explicit env var when set.
let API_BASE = process.env.REACT_APP_API_URL || '';
if (!API_BASE) {
  if (isCapacitor) {
    API_BASE = 'https://cloud.abstechnologies.co.in/api';
  } else if (host === 'localhost' || host === '127.0.0.1' || host === '::1' || port === '3000') {
    // Local dev: call backend on port 5000 (default NestJS dev port)
    API_BASE = 'http://127.0.0.1:5000/api';
  } else {
    API_BASE = '/api';
  }
}
console.info('[API] Resolved API_BASE =', API_BASE, 'host=', host, 'port=', port);

// ── Debug Log System ──
const DEBUG_LOG_KEY = 'abs_debug_logs';
const MAX_LOGS = 30;
export const debugLog = (msg: string) => {
  try {
    const logs = JSON.parse(localStorage.getItem(DEBUG_LOG_KEY) || '[]');
    logs.unshift(`[${new Date().toLocaleTimeString()}] ${msg}`);
    if (logs.length > MAX_LOGS) logs.length = MAX_LOGS;
    localStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(logs));
  } catch {}
};
export const getDebugLogs = (): string[] => {
  try { return JSON.parse(localStorage.getItem(DEBUG_LOG_KEY) || '[]'); } catch { return []; }
};
export const clearDebugLogs = () => localStorage.removeItem(DEBUG_LOG_KEY);

debugLog(`API_BASE=${API_BASE} host=${host}`);

// Get auth token from localStorage (persists across refreshes)
export const getToken = (): string | null => {
  const tokenData = localStorage.getItem('abs_token_data');
  if (!tokenData) return null;

  try {
    const { token } = JSON.parse(tokenData);
    return token || null;
  } catch {
    localStorage.removeItem('abs_token_data');
    return null;
  }
};

// Update last activity timestamp
export const updateLastActivity = () => {
  const tokenData = localStorage.getItem('abs_token_data');
  if (tokenData) {
    try {
      const { token } = JSON.parse(tokenData);
      localStorage.setItem('abs_token_data', JSON.stringify({ token, lastActivity: Date.now() }));
    } catch {
      // Ignore errors
    }
  }
};

// Store token with timestamp
export const storeToken = (token: string) => {
  localStorage.setItem('abs_token_data', JSON.stringify({ token, lastActivity: Date.now() }));
};

// Clear token
export const clearToken = () => {
  localStorage.removeItem('abs_token_data');
  sessionStorage.removeItem('abs_token'); // Clean up old storage
};

// Custom API Error
export class ApiError extends Error {
  constructor(public message: string, public status: number) {
    super(message);
    this.name = 'ApiError';
  }
}

// Generic fetch wrapper with auth
async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });
  } catch (err: any) {
    debugLog(`FETCH FAIL ${endpoint} → ${err.message}`);
    throw err;
  }

  if (!response.ok) {
    let error: any = { message: 'Request failed' };
    try {
      error = await response.json();
    } catch {
      try {
        const txt = await response.text();
        error = { message: txt };
      } catch {
        error = { message: 'Request failed' };
      }
    }
    // Auto-logout on session expiry (401) — except for login/auth endpoints
    if (response.status === 401 && !endpoint.includes('/auth/login')) {
      const currentPath = window.location.pathname;
      const onLoginPage = currentPath.toLowerCase().includes('/login');
      
      // Determine if this is a "soft" background request (e.g. state checks, active sessions)
      // These should NEVER trigger a redirect loop as they are often polled.
      const isSoftEndpoint =
        endpoint.includes('/sessions/active') ||
        endpoint.includes('/attendance/status') ||
        endpoint.includes('/tdl/connect/pending') ||
        endpoint.includes('/notifications/') ||
        endpoint.includes('/users'); // Often causes issues during initialization

      // Tracking log to help diagnose auth issues
      console.warn(`[AUTH TRACKER] 401 on ${endpoint} while on ${currentPath} (Soft Target: ${isSoftEndpoint})`);

      // Silent endpoints — never redirect on 401 (background polling)
      const isSilentEndpoint = endpoint.includes('/notifications/');

      if (onLoginPage || endpoint.includes('/auth/login')) {
        // We are already trying to log in. Don't interrupt with redirects.
        console.info('[AUTH TRACKER] 401 suppressed (Currently on login page or attempting login).');
      } else if (isSilentEndpoint) {
        // Background polling — don't redirect (user may be mid-task), but
        // clear the dead token so we stop hammering the server with it.
        // When the server nightly cron wipes cloud_user_sessions at 23:50,
        // this is what stops the APK from spamming 401s all night.
        console.warn(`[AUTH TRACKER] 401 on silent endpoint ${endpoint}. Clearing stale token.`);
        clearToken();
      } else if (isSoftEndpoint || endpoint.includes('/me')) {
        // These are background calls. If they fail, we should probably check if we need to logout.
        // Actually, if /me fails on the dashboard, it's a critical session failure.
        console.error(`[AUTH TRACKER] Session failure on ${endpoint}. Redirecting.`);
        clearToken();
        window.location.replace('/login');
      } else {
        // Critical app call failed 401. Logout immediately.
        console.error('[AUTH TRACKER] Critical 401. Redirecting to login.');
        clearToken();
        window.location.replace('/login');
      }
    }

    throw new ApiError(error.message || `HTTP ${response.status}`, response.status);
  }

  return response.json();
}

// Auth API
export const authApi = {
  login: (email: string, password: string, otp?: string, secret?: string) => {
    // 'mobile' gets the shorter 8h inactivity TTL; 'web' gets 24h.
    const device_type = (window as any).Capacitor?.isNativePlatform?.() ? 'mobile' : 'web';
    return fetchApi<{
      success: boolean;
      message: string;
      token: string;
      user: any;
      require_2fa?: boolean;
      setup_2fa?: boolean;
      secret?: string;
      otpauthUrl?: string
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, otp, secret, device_type }),
    });
  },

  checkHealth: () => fetchApi<any>('/auth/health'),

  getProfile: () => fetchApi<any>('/auth/me'),

  generate2FA: () => fetchApi<{ secret: string; otpauthUrl: string }>('/auth/2fa/generate', { method: 'POST' }),

  enable2FA: (token: string, secret: string) => fetchApi<{ success: boolean; message: string }>('/auth/2fa/enable', {
    method: 'POST',
    body: JSON.stringify({ token, secret }),
  }),

  disable2FA: () => fetchApi<{ success: boolean; message: string }>('/auth/2fa/disable', { method: 'POST' }),

  changePassword: (currentPass: string, newPass: string, otp: string) =>
    fetchApi<{ success: boolean; message: string }>('/auth/profile/password', {
      method: 'POST',
      body: JSON.stringify({ currentPass, newPass, otp }),
    }),

  getActiveSessions: () =>
    fetchApi<{ count: number; sessions: any[] }>('/auth/sessions/active'),

  logout: () =>
    fetchApi<{ success: boolean }>('/auth/logout', { method: 'POST' }),

  unlockSession: (otp: string) =>
    fetchApi<{ success: boolean; message: string }>('/auth/session/unlock', {
      method: 'POST',
      body: JSON.stringify({ otp }),
    }),
};

// Servers API
export const serversApi = {
  getAll: (page: number = 1, limit: number = 50, search: string = '', filters?: any) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) params.append('search', search);
    
    if (filters) {
      if (filters.company) params.append('company', filters.company);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.port) params.append('port', filters.port);
      if (filters.serverIp) params.append('server_ip', filters.serverIp);
      if (filters.customerIp) params.append('customer_ip', filters.customerIp);
      if (filters.adminUser) params.append('admin_username', filters.adminUser);
      if (filters.billing_mode && filters.billing_mode !== 'all') params.append('billing_mode', filters.billing_mode);
      if (filters.billing_cycle && filters.billing_cycle !== 'all') params.append('billing_cycle', filters.billing_cycle);
      if (filters.expiry_start) params.append('expiry_start', filters.expiry_start);
      if (filters.expiry_end) params.append('expiry_end', filters.expiry_end);
    }

    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(`/servers?${params.toString()}`);
  },
  search: (query: string) => fetchApi<{ success: boolean; data: any[] }>(`/servers?search=${encodeURIComponent(query)}&limit=20`),
  getDropdown: () => fetchApi<{ success: boolean; data: any[] }>('/servers/dropdown'),
  getById: (id: string) => fetchApi<{ success: boolean; data: any }>(`/servers/${id}`),
  create: (data: any) => fetchApi<{ success: boolean; data: any }>('/servers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => fetchApi<{ success: boolean; data: any }>(`/servers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`/servers/${id}`, {
    method: 'DELETE',
  }),
};

// Admins API - Fetches from admin table for Group dropdown
export const adminsApi = {
  getAll: (search: string = '') => fetchApi<any[]>(`/admins?search=${encodeURIComponent(search)}`),
};

// User Mapping API (admin-only)
export const userMappingApi = {
  getLegacyAdmins: () => fetchApi<{ success: boolean; data: any[] }>('/customers/mapping/legacy-admins'),
  getCloudUsers: () => fetchApi<{ success: boolean; data: any[] }>('/customers/mapping/cloud-users'),
  getCustomersByAdmin: (adminId: number, page: number = 1, limit: number = 50, search: string = '') =>
    fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(
      `/customers/mapping/by-admin/${adminId}?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`
    ),
  applyMapping: (customerIds: number[], cloudGroupId?: string, subgroupId?: string) =>
    fetchApi<{ success: boolean; updated: number; message: string }>('/customers/mapping/apply', {
      method: 'POST',
      body: JSON.stringify({ customerIds, cloudGroupId, subgroupId }),
    }),
};

// Group Change API
export const resellersApi = {
  getAll: (search: string = '') =>
    fetchApi<{ success: boolean; data: any[] }>(`/resellers${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  create: (data: { name: string; mobile?: string; email?: string; pan?: string; address?: string }) =>
    fetchApi<{ success: boolean; data: any; message: string }>('/resellers', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: number, data: { name?: string; mobile?: string; email?: string; pan?: string; address?: string }) =>
    fetchApi<{ success: boolean; message: string }>(`/resellers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  delete: (id: number) =>
    fetchApi<{ success: boolean; message: string }>(`/resellers/${id}`, { method: 'DELETE' }),
};

/** Which column the Group Transfer 'old group' picker filters on. 'ledger'
 *  matches customer.ledgergroup exactly — sub-group members are excluded. */
export type GroupSelectorType = 'cloud' | 'group' | 'ledger';

export const groupChangeApi = {
  getUsers: () => fetchApi<{ success: boolean; data: any[] }>('/group-change/users'),
  // Combined cloud + legacy groupings sourced from the customer table (with counts).
  getCustomerGroups: () => fetchApi<{ success: boolean; data: any[] }>('/group-change/customer-groups'),
  getCustomers: (userId: string) => fetchApi<{ success: boolean; data: any[] }>(`/group-change/customers?userId=${userId}`),
  transfer: (customerIds: number[], toUserId: string) =>
    fetchApi<{ success: boolean; transferred: number; message: string }>('/group-change/transfer', {
      method: 'POST',
      body: JSON.stringify({ customerIds, toUserId }),
    }),
  getResellers: () => fetchApi<{ success: boolean; data: any[] }>('/group-change/resellers'),
  transferReseller: (customerIds: number[], toResellerId: number | null) =>
    fetchApi<{ success: boolean; transferred: number; message: string }>('/group-change/transfer-reseller', {
      method: 'POST',
      body: JSON.stringify({ customerIds, toResellerId }),
    }),
  getHistory: (page: number = 1, limit: number = 50, search: string = '') =>
    fetchApi<{ success: boolean; data: any[]; total: number }>(`/group-change/history?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
  // Ledger group transfer helpers
  getLedgerGroups: () => fetchApi<{ success: boolean; data: any[] }>(`/ledger-groups`),
  transferLedgerGroup: (oldGroupId: string, newLedgerGroupId: number, resellerId?: number | null, groupType: GroupSelectorType = 'cloud') =>
    fetchApi<{ success: boolean; transferred: number; resellerUpdated?: number; message: string }>('/group-change/transfer-ledger-group', {
      method: 'POST',
      body: JSON.stringify({ oldGroupId, newLedgerGroupId, resellerId, groupType }),
    }),
  previewLedgerGroup: (oldGroupId: string, limit: number = 200, resellerId?: number | null, groupType: GroupSelectorType = 'cloud') =>
    fetchApi<{ success: boolean; total: number; rows: any[] }>(`/group-change/preview-ledger-group?oldGroupId=${encodeURIComponent(oldGroupId)}&limit=${limit}&groupType=${groupType}${resellerId ? `&resellerId=${resellerId}` : ''}`),
};

// One resolved phone number from the Call Log lookup. `others` holds any further
// customers sharing the same number, so the UI can show them instead of guessing.
export interface ContactLookupHit {
  contact_id: number | null;
  contact_person: string | null;
  mobile_no: string;
  customer_id: number;
  company: string;
  status: string;
  primary_contact: boolean;
  others: Omit<ContactLookupHit, 'others'>[];
}

/** A parsed bank statement line, as sent to the import API. */
export interface BankStatementRow {
  date?: string;
  /** Value date — the voucher is dated on this. */
  valueDate: string;
  narration: string;
  refNo?: string;
  withdrawal?: number;
  deposit?: number;
}

export interface BankImportPreviewRow extends BankStatementRow {
  key: string;
  kind: 'receipt' | 'payment';
  amount: number;
  postDate: string | null;
  duplicate: boolean;
  existingVchId?: number;
  error?: string;
}

/** One row of the Contacts phonebook search. */
export interface ContactSearchResult {
  contact_id: number | null;
  contact_person: string | null;
  mobile_no: string;
  customer_id: number;
  company: string;
  status: string;
  primary_contact: boolean;
}

// Customers API
export const customersApi = {
  // Contacts screen — one box over person name, company and number.
  searchContacts: (q: string, limit = 60) =>
    fetchApi<{ success: boolean; count: number; data: ContactSearchResult[] }>(
      `/customers/contacts/search?q=${encodeURIComponent(q)}&limit=${limit}`
    ),
  getAll: (page: number = 1, limit: number = 50, search: string = '', status: string = 'all', mappedOnly: boolean = false, aging: string = 'all', city: string = '', pincode: string = '', group: string = '', state: string = '', date_from: string = '', date_to: string = '', last_visit_person: string = '', sortBy: string = '', sortOrder: string = '', excludePendingVisits: boolean = false, extra: Record<string, string> = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      search,
      status,
      mapped_only: mappedOnly.toString()
    });
    if (aging !== 'all') params.append('aging', aging);
    if (city) params.append('city', city);
    if (pincode) params.append('pincode', pincode);
    if (group) params.append('group', group);
    if (state) params.append('state', state);
    if (date_from) params.append('date_from', date_from);
    if (date_to) params.append('date_to', date_to);
    if (last_visit_person) params.append('last_visit_person', last_visit_person);
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    if (excludePendingVisits) params.append('exclude_pending_visits', 'true');
    // New per-column filters (passed through as a flat map). Empty values
    // are dropped so the backend can default-skip them.
    for (const [k, v] of Object.entries(extra)) {
      if (v !== undefined && v !== null && String(v).trim() !== '') params.append(k, String(v));
    }
    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number; skip_counts?: Record<string, number> }>(`/customers?${params.toString()}`);
  },
  search: (query: string) => fetchApi<{ success: boolean; data: any[] }>(`/customers/autocomplete?q=${encodeURIComponent(query)}`),
  searchAllLedgers: (q: string) => fetchApi<{ success: boolean; data: any[] }>(`/customers/ledger-search?q=${encodeURIComponent(q)}`),
  searchDetail: (search: string, searchType: string) =>
    fetchApi<{ success: boolean; customers: any[] }>(`/customers/search-detail?search=${encodeURIComponent(search)}&search_type=${encodeURIComponent(searchType)}`),
  // Feeds the on-device caller-ID cache — the whole directory, keyed the same
  // way as lookupContacts so the handset can match without normalising.
  contactDirectory: () =>
    fetchApi<{ success: boolean; count: number; data: Array<{ n: string; p: string; c: string }> }>(
      `/customers/contacts/directory`
    ),
  // Call Log screen — resolves a page of dialled numbers in one round trip.
  // Keyed by the number's last 10 digits (see normalizeNumber in services/callLog).
  lookupContacts: (numbers: string[]) =>
    fetchApi<{ success: boolean; data: Record<string, ContactLookupHit> }>(`/customers/contacts/lookup`, {
      method: 'POST',
      body: JSON.stringify({ numbers }),
    }),
  createContact: (customerId: number, data: { contact_person: string; mobile_no: string; primary_contact: string }) =>
    fetchApi<{ success: boolean; data: any; message: string }>(`/customers/${customerId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  updateContactMapping: (customerId: number, contactId: number, data: { status?: string; primary_contact?: string }) =>
    fetchApi<{ success: boolean; message: string }>(`/customers/${customerId}/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  mapCompany: (customerId: number, targetCustomerId: number) =>
    fetchApi<{ success: boolean; message: string }>(`/customers/${customerId}/map-company`, {
      method: 'POST',
      body: JSON.stringify({ targetCustomerId }),
    }),
  getDropdown: () => fetchApi<{ success: boolean; data: any[] }>('/customers/dropdown'),
  getInactive: (opts: { search?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: 'ASC' | 'DESC' }) => {
    const q = new URLSearchParams();
    if (opts.search) q.set('search', opts.search);
    q.set('page', String(opts.page || 1));
    q.set('limit', String(opts.limit || 50));
    if (opts.sortBy) q.set('sortBy', opts.sortBy);
    if (opts.sortOrder) q.set('sortOrder', opts.sortOrder);
    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(
      `/customers/inactive?${q.toString()}`,
    );
  },
  reactivate: (id: number) =>
    fetchApi<{ success: boolean; message: string }>(`/customers/${id}/reactivate`, { method: 'POST' }),
  // Park a customer off the Visit Dashboard, or pass null to put them back.
  setVisitSkip: (id: number, reason: 'Personal Relation' | 'Business Closed' | 'Not Found' | null) =>
    fetchApi<{ success: boolean }>(`/customers/${id}/visit-skip`, { method: 'POST', body: JSON.stringify({ reason }) }),
  getHistory: (
    customerId: number,
    opts: { type: 'call' | 'visit' | 'service'; search?: string; date_from?: string; date_to?: string; page?: number; limit?: number },
  ) => {
    const q = new URLSearchParams();
    q.set('type', opts.type);
    if (opts.search) q.set('search', opts.search);
    if (opts.date_from) q.set('date_from', opts.date_from);
    if (opts.date_to) q.set('date_to', opts.date_to);
    q.set('page', String(opts.page || 1));
    q.set('limit', String(opts.limit || 20));
    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(
      `/customers/${customerId}/history?${q.toString()}`,
    );
  },
  getById: (id: string) => fetchApi<{ success: boolean; data: any }>(`/customers/${id}`),
  getLedgerGroups: () => fetchApi<{ success: boolean; data: Array<{ id: number; name: string; parent_id: number | null }> }>(`/customers/ledger-groups`),
  checkDuplicate: (params: { company?: string; gstin?: string; exclude_id?: string | number }) => {
    const q = new URLSearchParams();
    if (params.company) q.append('company', params.company);
    if (params.gstin) q.append('gstin', params.gstin);
    if (params.exclude_id) q.append('exclude_id', String(params.exclude_id));
    return fetchApi<{ success: boolean; data: { company_exists: boolean; gstin_exists: boolean } }>(`/customers/check-duplicate?${q.toString()}`);
  },
  getOpeningBills: (id: string) => fetchApi<{ success: boolean; data: { customer: any; bills: any[]; total: number } }>(`/customers/${id}/opening-bills`),
  saveOpeningBills: (id: string, bills: Array<{ bill_name: string; bill_date?: string | null; amount: number; ref_type?: 'Bill' | 'On Account' }>) =>
    fetchApi<{ success: boolean; message: string }>(`/customers/${id}/opening-bills`, {
      method: 'PUT',
      body: JSON.stringify({ bills }),
    }),
  create: (data: any) => fetchApi<{ success: boolean; data: any; message: string }>('/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => fetchApi<{ success: boolean; data: any }>(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`/customers/${id}`, {
    method: 'DELETE',
  }),
  getResellers: () => fetchApi<{ success: boolean; data: any[] }>('/customers/resellers'),
};

// Visits API
// A customer's sorting position, reachable without going through a visit — the
// customer reports have no visit to hang it off.
export const customerLocationApi = {
  get: (customerId: number) =>
    fetchApi<{
      success: boolean; customer_id: number; customer_name: string; address: string;
      checkin: { lat: number; lng: number } | null;
      map: { lat: number; lng: number; source?: string; precision?: string; label?: string; updated_at?: string } | null;
    }>(`/customers/${customerId}/map-location`),
  set: (customerId: number, link: string) =>
    fetchApi<{ success: boolean; map: { lat: number; lng: number } | null }>(
      `/customers/${customerId}/map-location`,
      { method: 'POST', body: JSON.stringify({ link }) },
    ),
};

export const visitsApi = {
  create: (data: any) => fetchApi<{ success: boolean; data: any }>('/visits/create', { method: 'POST', body: JSON.stringify(data) }),
  getPending: (userName: string) => fetchApi<any[]>(`/visits/pending?user_name=${encodeURIComponent(userName)}`),
  /** Visit counts per rep, grouped by check-out response. Takes the same filters as getAll. */
  getResponseSummary: (filters?: any) => {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.user_name && filters.user_name !== 'all') params.append('user_name', filters.user_name);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    return fetchApi<{ success: boolean; data: { user_name: string; response: string; total: number }[] }>(`/visits/response-summary?${params.toString()}`);
  },
  getAll: (page: number = 1, limit: number = 20, filters?: any, sortBy?: string, sortOrder?: string) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.user_name) params.append('user_name', filters.user_name);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(`/visits/all?${params.toString()}`);
  },
  pause: (id: number) => fetchApi<{ success: boolean }>('/visits/pause', { method: 'POST', body: JSON.stringify({ id }) }),
  resume: (id: number) => fetchApi<{ success: boolean }>('/visits/resume', { method: 'POST', body: JSON.stringify({ id }) }),
  complete: (data: any) => fetchApi<{ success: boolean }>('/visits/complete', { method: 'POST', body: JSON.stringify(data) }),
  delete: (id: number) => fetchApi(`/visits/${id}`, { method: 'DELETE' }),
  update: (data: any) => fetchApi<{ success: boolean }>('/visits/update', { method: 'POST', body: JSON.stringify(data) }),
  // Clears customer.lattitude/longitude so the next check-in re-seeds the geofence anchor.
  resetCustomerLocation: (id: number) => fetchApi<{ success: boolean; alreadyEmpty?: boolean; customer_name?: string; old_lat?: string; old_lng?: string; message?: string }>(`/visits/${id}/reset-location`, { method: 'POST' }),
  toggleForceCheckin: (id: number, allowed: boolean) => fetchApi<{ success: boolean }>('/visits/force-checkin', { method: 'POST', body: JSON.stringify({ id, allowed }) }),
  // The customer's sorting position, and where it came from. Used to order a
  // route; never read by the check-in geofence.
  getLocation: (visitId: number) =>
    fetchApi<{
      success: boolean; customer_id: number; customer_name: string; address: string;
      checkin: { lat: number; lng: number } | null;
      map: { lat: number; lng: number; source?: string; precision?: string; label?: string; updated_at?: string } | null;
    }>(`/visits/${visitId}/location`),

  // Set it from a pasted Google Maps link (or "lat, lng").
  setLocation: (visitId: number, link: string) =>
    fetchApi<{ success: boolean; map: { lat: number; lng: number } | null }>(
      `/visits/${visitId}/location`,
      { method: 'POST', body: JSON.stringify({ link }) },
    ),

  // Look up map coordinates for pending visits whose customer has none. Results
  // are cached on the customer, so calling this twice costs nothing the second time.
  locatePending: (user_name?: string, force = false) =>
    fetchApi<{ located: number; failed: number; skipped: number; source: 'google' | 'osm' }>(
      '/visits/route/locate',
      { method: 'POST', body: JSON.stringify({ user_name, force }) },
    ),
  // Append one slice of a visit recording while the visit is still running, so
  // audio is durable from the first few seconds instead of only at check-out.
  uploadRecordingChunk: async (id: number, blob: Blob, session: string, seq: number) => {
    const form = new FormData();
    form.append('recording', blob, `visit-${id}-${seq}.webm`);
    form.append('session', session);
    form.append('seq', String(seq));
    const token = getToken();
    const base = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';
    const r = await fetch(`${base}/api/visits/${id}/recording/chunk`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const text = await r.text();
    let json: any;
    try { json = JSON.parse(text); } catch { json = { success: false, raw: text }; }
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${json?.message || text}`);
    return json as { success: boolean; path?: string };
  },
  uploadRecording: async (id: number, blob: Blob, ext: string) => {
    const form = new FormData();
    form.append('recording', blob, `visit-${id}${ext}`);
    const token = getToken();
    const base = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';
    const r = await fetch(`${base}/api/visits/${id}/recording`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const text = await r.text();
    let json: any;
    try { json = JSON.parse(text); } catch { json = { success: false, raw: text }; }
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${json?.message || text}`);
    return json as { success: boolean; path?: string };
  },
  // Always go through the guarded endpoint. The /uploads static route serves
  // recordings to anyone who knows the filename, which defeats the
  // managers-and-admins-only rule this endpoint enforces.
  getRecordingUrl: (id: number) => `${API_BASE}/visits/${id}/recording`,
};

// Customer Calls API
export const callsApi = {
  create: (data: any) => fetchApi<{ success: boolean; data: any }>('/calls/create', { method: 'POST', body: JSON.stringify(data) }),
  getAll: (page: number = 1, limit: number = 20, filters?: any, sortBy?: string, sortOrder?: string) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.user_name) params.append('user_name', filters.user_name);
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (sortBy) params.append('sortBy', sortBy);
    if (sortOrder) params.append('sortOrder', sortOrder);
    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(`/calls?${params.toString()}`);
  },
  getById: (id: number) => fetchApi<{ success: boolean; data: any }>(`/calls/${id}`),
};

// Mappings API
export const mappingsApi = {
  getAll: (page: number = 1, limit: number = 50, serverId?: string, search: string = '', filters?: any, sort?: any) => {
    let url = `/mappings?page=${page}&limit=${limit}`;
    if (serverId) url += `&server_id=${serverId}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    
    // Filters
    if (filters?.status) url += `&status=${filters.status}`;
    if (filters?.billing_mode) url += `&billing_mode=${filters.billing_mode}`;
    if (filters?.billing_cycle) url += `&billing_cycle=${filters.billing_cycle}`;
    if (filters?.expiry_start) url += `&expiry_start=${filters.expiry_start}`;
    if (filters?.expiry_end) url += `&expiry_end=${filters.expiry_end}`;
    if (filters?.mapped_at_start) url += `&mapped_at_start=${filters.mapped_at_start}`;
    if (filters?.mapped_at_end) url += `&mapped_at_end=${filters.mapped_at_end}`;
    if (filters?.company) url += `&company=${encodeURIComponent(filters.company)}`;
    if (filters?.customer_ip) url += `&customer_ip=${encodeURIComponent(filters.customer_ip)}`;
    if (filters?.serial_no) url += `&serial_no=${encodeURIComponent(filters.serial_no)}`;
    if (filters?.min_rate) url += `&min_rate=${filters.min_rate}`;
    if (filters?.max_rate) url += `&max_rate=${filters.max_rate}`;

    // Sort
    if (sort?.field) {
      url += `&sortBy=${sort.field}`;
      if (sort.dir) url += `&sortDir=${sort.dir}`;
    }

    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(url);
  },
  getById: (id: string) => fetchApi<{ success: boolean; data: any }>(`/mappings/${id}`),
  getByCustomer: (customerId: string) => fetchApi<{ success: boolean; data: any }>(`/mappings/customer/${customerId}`),
  getAllByCustomer: (customerId: string) => fetchApi<{ success: boolean; data: any[] }>(`/mappings/customer/${customerId}/all`),
  getByServer: (serverId: string) => fetchApi<{ success: boolean; data: any[] }>(`/mappings?server_id=${serverId}`),
  getUnmapped: () => fetchApi<{ success: boolean; data: any[] }>('/mappings/unmapped-customers'),
  create: (data: any) => fetchApi<{ success: boolean; data: any }>('/mappings', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => fetchApi<{ success: boolean; data: any }>(`/mappings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`/mappings/${id}`, {
    method: 'DELETE',
  }),
};

// Activities API
export const activitiesApi = {
  getAll: (filters?: any, page: number = 1, limit: number = 50, search: string = '') => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (search) params.append('search', search);
    if (filters?.activity_type) params.append('activity_type', filters.activity_type);
    if (filters?.bill_type) params.append('bill_type', filters.bill_type);
    if (filters?.customer_id) params.append('customer_id', filters.customer_id);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.record_nature) params.append('record_nature', filters.record_nature);
    if (filters?.server_name) params.append('server_name', filters.server_name);
    if (filters?.billing_cycle) params.append('billing_cycle', filters.billing_cycle);
    if (filters?.billing_mode) params.append('billing_mode', filters.billing_mode);
    if (filters?.min_amount !== undefined && filters?.min_amount !== '') params.append('min_amount', filters.min_amount.toString());
    if (filters?.max_amount !== undefined && filters?.max_amount !== '') params.append('max_amount', filters.max_amount.toString());
    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(`/activities?${params.toString()}`);
  },
  getById: (id: string) => fetchApi<{ success: boolean; data: any }>(`/activities/${id}`),
  getByCustomer: (customerId: string) => fetchApi<{ success: boolean; data: any[] }>(`/activities/customer/${customerId}`),
  getByVoucher: (voucherId: number) => fetchApi<{ success: boolean; data: Array<{ id: string; activity_type: string; server_name: string | null; customer_domain_ip: string | null; sof_no: string | null; billing_units: number; billing_cycle: string | null; billing_mode: string | null; last_bill_rate: number | string | null; start_from: string | null; old_expiry_date: string | null; new_expiry_date: string | null; bill_amount: number | string | null }> }>(`/activities/by-voucher/${voucherId}`),
  getStats: (filters?: any, search: string = '') => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (filters?.activity_type) params.append('activity_type', filters.activity_type);
    if (filters?.bill_type) params.append('bill_type', filters.bill_type);
    if (filters?.customer_id) params.append('customer_id', filters.customer_id);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.record_nature) params.append('record_nature', filters.record_nature);
    if (filters?.server_name) params.append('server_name', filters.server_name);
    if (filters?.billing_cycle) params.append('billing_cycle', filters.billing_cycle);
    if (filters?.billing_mode) params.append('billing_mode', filters.billing_mode);
    if (filters?.min_amount !== undefined && filters?.min_amount !== '') params.append('min_amount', filters.min_amount.toString());
    if (filters?.max_amount !== undefined && filters?.max_amount !== '') params.append('max_amount', filters.max_amount.toString());
    return fetchApi<{ success: boolean; data: {
      new: { count: number; units_total: number; amount_total: number };
      renewal: { count: number; units_total: number; amount_total: number };
      user_increase: { count: number; units_total: number; amount_total: number };
      user_decrease: { count: number; units_total: number; amount_total: number };
    } }>(`/activities/stats?${params.toString()}`);
  },
  getRevenueSummary: () => fetchApi<{ success: boolean; data: any }>('/activities/revenue'),
  getRenewalDefaults: (id: string, type: 'customer' | 'server', serverName?: string, mappingId?: string) => fetchApi<{ success: boolean; data: any }>(`/activities/renewal-defaults?id=${id}&type=${type}${serverName ? `&server_name=${encodeURIComponent(serverName)}` : ''}${mappingId ? `&mapping_id=${encodeURIComponent(mappingId)}` : ''}`),
  // New calculation endpoint - moves calculations to backend
  calculate: (data: {
    activity_type: 'New' | 'Renewal' | 'User';
    bill_type: 'Tax Invoice' | 'Credit Note';
    billing_units: number;
    purchase_units?: number;
    last_bill_rate: number;
    purchase_rate?: number;
    billing_cycle: 'Monthly' | 'Quarterly' | 'Half-Yearly' | 'Yearly';
    activity_date?: string;
    start_from?: string;
    new_expiry_date?: string;
    customer_id?: string;
    billing_mode?: 'day_to_day' | 'month_to_month';
    custom_period?: boolean;
    purchase_billing_mode?: 'day_to_day' | 'month_to_month';
    purchase_cycle?: string;
    purchase_start_from?: string;
    purchase_expiry?: string;
  }) => fetchApi<{
    success: boolean;
    data: {
      bill_amount: number;
      purchase_amount: number;
      date_diff_months: number;
      date_diff_days: number;
      date_diff_label: string;
      purchase_date_diff_months: number;
      purchase_date_diff_days: number;
      purchase_date_diff_label: string;
      new_expiry_date: string | null;
      formula_breakdown: string;
    }
  }>('/activities/calculate', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getTotalUsers: (customerId: string) => fetchApi<{ success: boolean; data: { total_users: number } }>(`/activities/customer/${customerId}/total-users`),
  getLastExpiry: (customerId: string) => fetchApi<{ success: boolean; data: { last_expiry_date: string | null } }>(`/activities/customer/${customerId}/last-expiry`),
  getPendingByCustomer: (customerId: string, voucherId?: number) => fetchApi<{ success: boolean; data: any[] }>(`/activities/customer/${customerId}/pending${voucherId ? `?voucher_id=${voucherId}` : ''}`),
  getPendingCreditNoteByCustomer: (customerId: string, voucherId?: number) => fetchApi<{ success: boolean; data: any[] }>(`/activities/customer/${customerId}/pending-credit-note${voucherId ? `?voucher_id=${voucherId}` : ''}`),
  getPendingPurchaseByCustomer: (customerId: string, voucherId?: number) => fetchApi<{ success: boolean; data: any[] }>(`/activities/customer/${customerId}/pending-purchase${voucherId ? `?voucher_id=${voucherId}` : ''}`),
  markBilled: (activityIds: string[], opts: { voucherId?: number; voucherNo?: string }) => fetchApi<{ success: boolean; updated: number }>('/activities/mark-billed', {
    method: 'POST',
    body: JSON.stringify({ activity_ids: activityIds, voucher_id: opts.voucherId, voucher_no: opts.voucherNo }),
  }),

  create: (data: any) => fetchApi<{ success: boolean; data: any }>('/activities', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => fetchApi<{ success: boolean; data: any }>(`/activities/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`/activities/${id}`, {
    method: 'DELETE',
  }),
  // Bulk generate activities for selected servers (P.U - Purchase Units)
  generateForServers: (serverIds: string[], purchaseRate?: number) =>
    fetchApi<{
      success: boolean;
      data: {
        created: any[];
        skipped: { server_id: string; reason: string }[];
      };
      message: string;
    }>('/activities/generate-for-servers', {
      method: 'POST',
      body: JSON.stringify({ server_ids: serverIds, purchase_rate: purchaseRate }),
    }),
  // Bulk generate renewal activities for selected customers (B.U - Billing Units)
  bulkCustomerRenewal: (customerIds: string[], activityDate?: string) =>
    fetchApi<{
      success: boolean;
      data: {
        created: any[];
        skipped: { customer_id: string; customer_name: string; reason: string }[];
      };
      message: string;
    }>('/activities/bulk-customer-renewal', {
      method: 'POST',
      body: JSON.stringify({ customer_ids: customerIds, activity_date: activityDate }),
    }),
};

// Users API
export const usersApi = {
  getBasic: () => fetchApi<{ success: boolean; data: any[] }>('/users/basic'),
  getAll: () => fetchApi<{ success: boolean; data: any[] }>('/users'),
  getById: (id: string) => fetchApi<{ success: boolean; data: any }>(`/users/${id}`),
  create: (data: any) => fetchApi<{ success: boolean; data: any }>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: string, data: any) => fetchApi<{ success: boolean; data: any }>(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  updatePassword: (id: string, password: string) => fetchApi<{ success: boolean }>(`/users/${id}/password`, {
    method: 'PUT',
    body: JSON.stringify({ password }),
  }),
  updatePermissions: (id: string, permissions: any) => fetchApi<{ success: boolean; data: any }>(`/users/${id}/permissions`, {
    method: 'PUT',
    body: JSON.stringify({ permissions }),
  }),
  updateColumnPermissions: (id: string, column_permissions: any) => fetchApi<{ success: boolean; data: any }>(`/users/${id}/column-permissions`, {
    method: 'PUT',
    body: JSON.stringify({ column_permissions }),
  }),
  getNetwork: () => fetchApi<{ success: boolean; data: any[] }>('/users/network'),
  updateLocation: (lat: number, lng: number) => fetchApi<{ success: boolean }>('/users/location', {
    method: 'POST',
    body: JSON.stringify({ lat, lng }),
  }),
  reset2FA: (id: string) => fetchApi<{ success: boolean }>(`/users/${id}/2fa/reset`, {
    method: 'POST',
  }),
  generate2FA: (id: string) =>
    fetchApi<{ secret: string; otpauthUrl: string }>(`/users/${id}/2fa/generate`, {
      method: 'POST',
    }),
  enable2FA: (id: string, secret: string, token: string) =>
    fetchApi<{ success: boolean }>(`/users/${id}/2fa/enable`, {
      method: 'POST',
      body: JSON.stringify({ secret, token }),
    }),
  delete: (id: string) => fetchApi<{ success: boolean }>(`/users/${id}`, {
    method: 'DELETE',
  }),
  getLocationHistory: (id: string, date: string) => fetchApi<{ success: boolean; data: any[] }>(`/users/${id}/location-history?date=${date}`),
  /** Street address at a coordinate. Cached server-side, so repeats are instant. */
  reverseGeocode: (lat: number, lng: number) =>
    fetchApi<{ success: boolean; data: { address: string | null } }>(`/users/reverse-geocode?lat=${lat}&lng=${lng}`),
  /** The day's pings grouped into numbered stops, with dwell time and address. */
  getLocationStops: (id: string, date: string) => fetchApi<{
    success: boolean;
    data: {
      seq: number; lat: number; lng: number;
      arrived_at: string; left_at: string;
      minutes: number; pings: number; travel_minutes: number;
      address: string | null;
    }[];
  }>(`/users/${id}/location-stops?date=${date}`),
};

// Dashboard API
/** Customer-movement window. 'custom' pairs with an explicit from/to range. */
export type MovementPeriod = 'today' | 'yesterday' | '30d' | 'custom';

/** Build the shared period/date query for the movement endpoints. The bounds
 *  are sent only for 'custom' — the server ignores them otherwise, and omitting
 *  them keeps the other three URLs byte-identical so the browser cache still
 *  hits on the common Today/Yesterday/30D toggles. */
function movementQuery(period: MovementPeriod, range?: { from?: string; to?: string }): URLSearchParams {
  const q = new URLSearchParams({ period });
  if (period === 'custom' && range?.from && range?.to) {
    q.set('date_from', range.from);
    q.set('date_to', range.to);
  }
  return q;
}

export const dashboardApi = {
  getStats: () => fetchApi<{ success: boolean; data: any }>('/dashboard/stats'),
  // `range` is only read when period === 'custom' (inclusive YYYY-MM-DD bounds).
  getOperationsSnapshot: (period: MovementPeriod = '30d', range?: { from?: string; to?: string }) => fetchApi<{ success: boolean; data: {
    expiry: {
      old:        { our: { silver: number; gold: number; auditor: number; total: number }; other: { silver: number; gold: number; auditor: number; total: number } };
      this_month: { our: { silver: number; gold: number; auditor: number; total: number }; other: { silver: number; gold: number; auditor: number; total: number } };
      future:     { our: { silver: number; gold: number; auditor: number; total: number }; other: { silver: number; gold: number; auditor: number; total: number } };
    };
    movement: {
      onboard_new:        { silver: number; gold: number; auditor: number; total: number };
      onboard_from_other: { silver: number; gold: number; auditor: number; total: number };
      left:               { silver: number; gold: number; auditor: number; total: number };
    };
  } }>(`/dashboard/operations-snapshot?${movementQuery(period, range).toString()}`),
  // Drill-down: serials + customer info behind one movement tile. The range must
  // match the one the tile was counted with, or the list won't match the count.
  getMovementDetail: (category: 'onboard_new' | 'onboard_from_other' | 'left', grade?: 'silver' | 'gold' | 'auditor', period: MovementPeriod = '30d', range?: { from?: string; to?: string }) => {
    const q = movementQuery(period, range);
    q.set('category', category);
    if (grade) q.set('grade', grade);
    return fetchApi<{ success: boolean; data: { category: string; grade: string; period: string; count: number; rows: Array<{ id: number; serial_no: string | null; grade: string; expiry_date: string | null; movement_date: string | null; tally_status: string | null; customer_id: number | null; customer_name: string; customer_mobile: string | null; customer_area: string | null }> } }>(`/dashboard/operations-snapshot/movement?${q.toString()}`);
  },
  myPerformance: (user?: string, fy?: string) => {
    const q = new URLSearchParams();
    if (user) q.set('user', user);
    if (fy) q.set('fy', fy);
    return fetchApi<any>(`/dashboard/my-performance?${q.toString()}`);
  },
  getPendingUsers: () => fetchApi<any>('/dashboard/pending-users'),
  getPendingDetail: (type: string, user: string) =>
    fetchApi<any>(`/dashboard/pending-detail?type=${encodeURIComponent(type)}&user=${encodeURIComponent(user)}`),
  adminPerformance: (fy?: string) => {
    const q = new URLSearchParams();
    if (fy) q.set('fy', fy);
    return fetchApi<any>(`/dashboard/admin-performance${q.toString() ? `?${q.toString()}` : ''}`);
  },
};

// TDL API
export const tdlApi = {
  getAllCustomizations: () => fetchApi<any[]>('/tdl/customizations'),
  getCustomizationById: (id: string) => fetchApi<any>(`/tdl/customizations/${id}`),
  lookupByToken: (token: string) => fetchApi<any>(`/tdl/lookup/${token}`),
  getTaskHistory: (taskId: number) => fetchApi<any[]>(`/tdl/tasks/${taskId}/history`),
  manageTasks: (reqId: number | string | null, tasks: any[]) => fetchApi<any>(`/tdl/requirements/${reqId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(tasks),
  }),
  getConnectPending: (userName: string) => fetchApi<any[]>(`/tdl/connect/pending?user_name=${encodeURIComponent(userName)}`),
  deleteTask: (taskId: number) => fetchApi(`/tdl/tasks/${taskId}`, { method: 'DELETE' }),
  addTaskUpdate: (taskId: number, data: { remark?: string; status?: string; next_date?: string }) =>
    fetchApi<any>(`/tdl/tasks/${taskId}/update`, { method: 'PUT', body: JSON.stringify(data) }),
  getTaskUpdates: (taskId: number) => fetchApi<any>(`/tdl/tasks/${taskId}/updates`),
};

// Pincode API
export const statesApi = {
  getAll: () => fetchApi<{ success: boolean; data: any[] }>('/states'),
};

export const pincodeApi = {
  getAll: (page: number = 1, limit: number = 50, search: string = '') => fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number }>(`/pincodes?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
  lookup: (code: string) => fetchApi<{ city: string; state: string }>(`/pincodes/lookup/${code}`),
  create: (data: any) => fetchApi<any>('/pincodes', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id: number, data: any) => fetchApi<any>(`/pincodes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id: number) => fetchApi<{ success: boolean }>(`/pincodes/${id}`, {
    method: 'DELETE',
  }),
};

// Service Calls API
export const serviceCallsApi = {
  getAll: (status?: string, search?: string, startDate?: string, endDate?: string, staff?: string, entryType?: string) => {
    let url = `/service-calls?status=${status || 'all'}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    if (staff) url += `&staff=${encodeURIComponent(staff)}`;
    if (entryType) url += `&entryType=${encodeURIComponent(entryType)}`;
    return fetchApi<{ success: boolean; data: any[]; total: number }>(url);
  },
  // Summary cards on Service Pending — each bucket's count, how many have
  // waited over a day, and the oldest one's start time.
  getPendingSummary: (staff?: string, type?: string) => {
    const q = new URLSearchParams();
    if (staff) q.set('staff', staff);
    if (type) q.set('type', type);
    type Bucket = { total: number; over_day: number; oldest: string | null };
    return fetchApi<{ success: boolean; data: Record<'unalloted' | 'pending' | 'followup' | 'refollowup', Bucket> & { completed: { today: number; month: number; total: number } } }>(`/service-calls/pending-summary?${q.toString()}`);
  },
  getStats: (startDate?: string, endDate?: string, staff?: string, entryType?: string) => {
    let url = '/service-calls/stats';
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (staff) params.append('staff', staff);
    if (entryType) params.append('entryType', entryType);

    if (params.toString()) url += `?${params.toString()}`;
    return fetchApi<{ success: boolean; data: { total: number; open: number; pending: number; closed: number; cancelled: number; my_pending: number; my_completed: number; my_cancelled: number } }>(url);
  },
  getFlavors: () => fetchApi<{ success: boolean; data: { id: number; name: string }[] }>('/service-calls/flavors'),
  lookupContact: (mobile: string) => fetchApi<{ success: boolean; found: boolean; contact: { id: number; contact_person: string; mobile_no: string } | null }>(`/service-calls/lookup/${mobile}`),
  lookupTallySerial: (serial: string) => fetchApi<{ success: boolean; data: { customer_id: number; customer_name: string; flavor: string; flavor_name: string; expire_date: string } }>(`/service-calls/lookup-tally/${serial}`),
  create: (data: {
    mobile_no: string;
    contact_person?: string;
    service_type?: string;
    remark?: string;
    assign_to?: string;
    flavor?: string;
    customer_id?: string | number;
    serial_number?: string;
    expire_date?: string;
  }) => fetchApi<{ success: boolean; data: any; message: string }>('/service-calls', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  take: (id: number) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/take`, {
    method: 'PUT',
    body: JSON.stringify({}),
  }),
  transfer: (id: number, assign_to: string) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/transfer`, {
    method: 'PUT',
    body: JSON.stringify({ assign_to }),
  }),
  confirm: (id: number, satisfaction_rating?: number) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/confirm`, {
    method: 'PUT',
    body: JSON.stringify({ satisfaction_rating }),
  }),
  reopen: (id: number, assignTo?: string) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/reopen`, {
    method: 'PUT',
    body: JSON.stringify({ assign_to: assignTo || null }),
  }),
  cancel: (id: number) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/cancel`, {
    method: 'PUT',
    body: JSON.stringify({}),
  }),
  getReportsUserWise: (startDate?: string, endDate?: string) => {
    let url = '/service-calls/reports/user-wise';
    const q: string[] = [];
    if (startDate) q.push(`startDate=${startDate}`);
    if (endDate) q.push(`endDate=${endDate}`);
    if (q.length > 0) url += `?${q.join('&')}`;
    return fetchApi<{ success: boolean; data: any[] }>(url);
  },
  getReportsDelays: (startDate?: string, endDate?: string) => {
    let url = '/service-calls/reports/delays';
    const q: string[] = [];
    if (startDate) q.push(`startDate=${startDate}`);
    if (endDate) q.push(`endDate=${endDate}`);
    if (q.length > 0) url += `?${q.join('&')}`;
    return fetchApi<{ success: boolean; data: any[] }>(url);
  },
  close: (id: number, data: {
    customer_id?: string | number;
    contact_person?: string;
    serial_number?: string;
    service_type?: string;
    remark?: string;
    expire_date?: string;
    flavor?: string;
    resolution_note?: string;
    assigned_developer?: string;
  }) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/close`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  join: (id: number, data: {
    customer_id?: number | null; contact_person?: string; serial_number?: string;
    service_type?: string; remark?: string; expire_date?: string; flavor?: string;
    assigned_developer?: string;
  }) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/join`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getNotes: (id: number) => fetchApi<{ success: boolean; data: any[] }>(`/service-calls/${id}/notes`),
  addNote: (id: number, data: { note_type: string; content: string; assigned_to?: string; deadline?: string; next_update_date?: string }) => fetchApi<{ success: boolean; message: string }>(`/service-calls/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateNoteStatus: (noteId: number, status: string) => fetchApi<{ success: boolean; message: string }>(`/service-calls/notes/${noteId}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),
};

// Billing API
export const billingApi = {
  // Bills
  createBill: (data: any) => fetchApi<{ success: boolean; data: any; message: string }>('/billing/bills', { method: 'POST', body: JSON.stringify(data) }),
  getBills: (params: { bill_type?: string; bill_status?: string; pay_status?: string; search?: string; startDate?: string; endDate?: string; reseller?: boolean; no_follow?: boolean; today?: boolean; after_today?: boolean }) => {
    const q = new URLSearchParams();
    if (params.bill_type) q.append('bill_type', params.bill_type);
    if (params.bill_status) q.append('bill_status', params.bill_status);
    if (params.pay_status) q.append('pay_status', params.pay_status);
    if (params.search) q.append('search', params.search);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.reseller) q.append('reseller', 'true');
    if (params.no_follow) q.append('no_follow', 'true');
    if (params.today) q.append('today', 'true');
    if (params.after_today) q.append('after_today', 'true');
    return fetchApi<{ success: boolean; data: any[] }>(`/billing/bills?${q.toString()}`);
  },
  getBill: (id: number) => fetchApi<{ success: boolean; data: any }>(`/billing/bills/${id}`),
  updateBill: (id: number, data: any) => fetchApi<{ success: boolean; message: string }>(`/billing/bills/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateBillStatus: (id: number, data: any) => fetchApi<{ success: boolean; message: string }>(`/billing/bills/${id}/status`, { method: 'PUT', body: JSON.stringify(data) }),
  incrementFollowup: (id: number) => fetchApi<{ success: boolean; message: string }>(`/billing/bills/${id}/followup`, { method: 'PUT', body: JSON.stringify({}) }),
  // Payments
  addPayment: (data: any) => fetchApi<{ success: boolean; data: any; message: string }>('/billing/payments', { method: 'POST', body: JSON.stringify(data) }),
  getPayments: (params: { status?: string; search?: string; startDate?: string; endDate?: string; payment_complete?: string }) => {
    const q = new URLSearchParams();
    if (params.status) q.append('status', params.status);
    if (params.search) q.append('search', params.search);
    if (params.startDate) q.append('startDate', params.startDate);
    if (params.endDate) q.append('endDate', params.endDate);
    if (params.payment_complete) q.append('payment_complete', params.payment_complete);
    return fetchApi<{ success: boolean; data: any[] }>(`/billing/payments?${q.toString()}`);
  },
  updatePayment: (id: number, data: any) => fetchApi<{ success: boolean; message: string }>(`/billing/payments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  // Lookups
  getBillingCompanies: () => fetchApi<{ success: boolean; data: any[] }>('/billing/billing-companies'),
  getBillingCompanyItems: (companyId: number) => fetchApi<{ success: boolean; data: any[] }>(`/billing/billing-companies/${companyId}/items`),
  getProducts: () => fetchApi<{ success: boolean; data: any[] }>('/billing/products'),
  getTallyItemTypes: () => fetchApi<{ success: boolean; data: any[] }>('/billing/tally-item-types'),
};

export type LedgerGroupCommissionRow = { item_id: number; item_name?: string; commission_type: 'qty' | 'value'; value: number };
export const ledgerGroupApi = {
  getAll: () => fetchApi<{ success: boolean; data: any[] }>('/ledger-groups'),
  getCommissions: (id: number) => fetchApi<{ success: boolean; data: LedgerGroupCommissionRow[] }>(`/ledger-groups/${id}/commissions`),
  create: (data: { name: string; parent_id?: number | null; commissions?: LedgerGroupCommissionRow[] }) => fetchApi<{ success: boolean; data: any; message: string }>('/ledger-groups', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: { name?: string; parent_id?: number | null; commissions?: LedgerGroupCommissionRow[] }) => fetchApi<{ success: boolean; message: string }>(`/ledger-groups/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchApi<{ success: boolean; message: string }>(`/ledger-groups/${id}`, { method: 'DELETE' }),
};

/** Admin-only "Reset Tally Data" actions — each clears the Tally sync stamp on
 *  a whole master (or, for vouchers, an optional period) so those rows come
 *  back in the corresponding /tally-pending feed and get re-created in Tally. */
export const tallyResetApi = {
  ledgerGroups: () => fetchApi<{ success: boolean; reset: number }>('/ledger-groups/tally-reset', { method: 'POST' }),
  customers:    () => fetchApi<{ success: boolean; reset: number }>('/customers/tally-reset', { method: 'POST' }),
  itemGroups:   () => fetchApi<{ success: boolean; reset: number }>('/items/groups/tally-reset', { method: 'POST' }),
  items:        () => fetchApi<{ success: boolean; reset: number }>('/items/tally-reset', { method: 'POST' }),
  vouchers: (params: { date_from?: string; date_to?: string; vch_type?: string } = {}) =>
    fetchApi<{ success: boolean; reset: number }>('/vouchers/tally-reset', { method: 'POST', body: JSON.stringify(params) }),
};

export const vchTypeApi = {
  getAll: () => fetchApi<{ success: boolean; data: any[] }>('/vchtypes'),
  create: (data: any) => fetchApi<{ success: boolean; data: any; message: string }>('/vchtypes', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => fetchApi<{ success: boolean; message: string }>(`/vchtypes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchApi<{ success: boolean; message: string }>(`/vchtypes/${id}`, { method: 'DELETE' }),
  getAudit: (id: number) => fetchApi<{ success: boolean; data: any[] }>(`/vchtypes/${id}/audit`),
};

export const otherLedgerApi = {
  // scope='all' returns the full chart of accounts unscoped (voucher entry);
  // default is scoped to the user's ledger group (Ledgers management screen).
  getAll: (scope?: 'all') => fetchApi<{ success: boolean; data: any[] }>(`/other-ledgers${scope === 'all' ? '?scope=all' : ''}`),
  create: (data: { company: string; ledgergroup: number; opening_balance?: number; opening_balance_type?: string; billbybill?: string }) => fetchApi<{ success: boolean; data: any; message: string }>('/other-ledgers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: { company?: string; ledgergroup?: number; opening_balance?: number; opening_balance_type?: string; billbybill?: string }) => fetchApi<{ success: boolean; message: string }>(`/other-ledgers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchApi<{ success: boolean; message: string }>(`/other-ledgers/${id}`, { method: 'DELETE' }),
};

export const itemsApi = {
  getAll: () => fetchApi<{ success: boolean; data: any[] }>('/items'),
  getFlavours: () => fetchApi<{ success: boolean; data: { id: number; name: string }[] }>('/items/flavours'),
  create: (data: any) => fetchApi<{ success: boolean; data: any; message: string }>('/items', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => fetchApi<{ success: boolean; message: string }>(`/items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => fetchApi<{ success: boolean; message: string }>(`/items/${id}`, { method: 'DELETE' }),
  getCategories: () => fetchApi<{ success: boolean; data: any[] }>('/items/categories'),
  createCategory: (name: string, parent_id?: number | null, target_unit?: string) => fetchApi<{ success: boolean; data: any }>('/items/categories', { method: 'POST', body: JSON.stringify({ name, parent_id, target_unit }) }),
  updateCategory: (id: number, name: string, parent_id?: number | null, target_unit?: string) => fetchApi<{ success: boolean }>(`/items/categories/${id}`, { method: 'PUT', body: JSON.stringify({ name, parent_id, target_unit }) }),
  deleteCategory: (id: number) => fetchApi<{ success: boolean }>(`/items/categories/${id}`, { method: 'DELETE' }),
  getOpeningBatches: (id: number) => fetchApi<{ success: boolean; data: any[] }>(`/items/${id}/opening-batches`),
  saveOpeningBatches: (id: number, batches: any[]) => fetchApi<{ success: boolean }>(`/items/${id}/opening-batches`, { method: 'POST', body: JSON.stringify({ batches }) }),
  getGroups: () => fetchApi<{ success: boolean; data: any[] }>('/items/groups'),
  createGroup: (name: string, parent_id?: number | null) => fetchApi<{ success: boolean; data: any }>('/items/groups', { method: 'POST', body: JSON.stringify({ name, parent_id }) }),
  updateGroup: (id: number, name: string, parent_id?: number | null) => fetchApi<{ success: boolean }>(`/items/groups/${id}`, { method: 'PUT', body: JSON.stringify({ name, parent_id }) }),
  deleteGroup: (id: number) => fetchApi<{ success: boolean }>(`/items/groups/${id}`, { method: 'DELETE' }),
};

/** Tally-style Profit & Loss returned alongside the root Group Summary.
 *  Every figure is the movement inside the selected date range. `null` for
 *  ledger-group-scoped users — a partial ledger set can't yield a company P&L. */
export interface GroupSummaryProfit {
  sales: number;
  direct_incomes: number;
  closing_stock: number;
  purchases: number;
  direct_expenses: number;
  opening_stock: number;
  gross_profit: number;
  indirect_incomes: number;
  indirect_expenses: number;
  nett_profit: number;
  /** Revenue/expense-looking root groups that couldn't be classified. */
  unclassified: string[];
  /** Which root group produced each figure — lets the report drill in. */
  sources: Array<{
    nature: 'sales' | 'direct_income' | 'purchase' | 'direct_expense' | 'indirect_income' | 'indirect_expense' | string;
    group_id: number;
    group_name: string;
    debit: number;
    credit: number;
    /** Movement in the nature's natural direction (income = net credit). */
    amount: number;
  }>;
}

/** Synthetic lines that make a root listing foot to zero. Nominal accounts are
 *  replaced by the Profit & Loss A/c line (showing both would double-count),
 *  and whatever is left over is the opening-balance gap. */
export interface GroupSummaryBalanceSheet {
  /** Fold these away when balancing — group ids in the group view, root group
   *  ids in the ledger-wise view. */
  nominal_group_ids: number[];
  /** Ready-made rows to append, in display order. Closings sum with the
   *  remaining rows to exactly zero. */
  lines: Array<{
    key: 'closing_stock' | 'profit_and_loss' | 'opening_difference' | string;
    name: string;
    opening: number;
    debit: number;
    credit: number;
    /** Signed, Dr positive. Negative = Cr. */
    closing: number;
  }>;
  /** Prior-period balance sitting on nominal accounts, net of opening stock.
   *  Bridges the two profit figures on screen:
   *    period Nett Profit − brought_forward = P&L A/c closing. */
  brought_forward: number;
}

/** Balance Sheet, Tally style. Every figure is signed Dr-positive: a negative
 *  closing is a credit and belongs on the Liabilities side. Both sides foot to
 *  the same total. `null` profit_and_loss = a ledger-group-scoped user, who
 *  can't be shown a company balance sheet. */
export interface BalanceSheetReport {
  rows: Array<{ group_id: number; group_name: string; closing: number }>;
  profit_and_loss: {
    closing: number;
    opening: number;
    current_period: number;
  } | null;
  opening_difference: number;
  date_from: string | null;
  as_at: string | null;
}

/** Lightweight, admin-only view of vouchers that the Tally connector still
 * needs to import. Unlike the full tally-pending feed it deliberately omits
 * ledger and inventory payloads, so it is suitable for a portal report. */
export interface TallyPendingTransaction {
  id: number;
  vch_no: string | null;
  vch_date: string | null;
  vch_type: string | null;
  vch_subtype: string | null;
  party_name: string | null;
  amount: number;
  updated_at: string | null;
  tally_master_synced_at: string | null;
  pending_reason: 'New' | 'Updated after sync';
}

export interface TallyPendingTransactionReport {
  success: boolean;
  message?: string;
  total: number;
  page: number;
  limit: number;
  total_pages: number;
  totals: {
    voucher_count: number;
    total_amount: number;
    new_count: number;
    updated_count: number;
  };
  type_totals: Array<{
    vch_type: string;
    voucher_count: number;
    total_amount: number;
  }>;
  rows: TallyPendingTransaction[];
  /** Same rows as `rows`, retained for the API's common list-response shape. */
  data: TallyPendingTransaction[];
}

export const vouchersApi = {
  // Bank statement import. The file is parsed in the browser (SheetJS is
  // already bundled for exports), so the API takes normalised rows.
  bankImportPreview: (rows: BankStatementRow[], bank_ledger_id: number) =>
    fetchApi<{ success: boolean; data: BankImportPreviewRow[]; summary: { total: number; receipts: number; payments: number; duplicates: number; errors: number } }>(
      '/vouchers/bank-import/preview',
      { method: 'POST', body: JSON.stringify({ rows, bank_ledger_id }) }
    ),
  bankImportCommit: (rows: BankStatementRow[], bank_ledger_id: number, suspense_ledger_id: number) =>
    fetchApi<{ success: boolean; imported: number; skipped: number; failed: number; errors: string[] }>(
      '/vouchers/bank-import/commit',
      { method: 'POST', body: JSON.stringify({ rows, bank_ledger_id, suspense_ledger_id }) }
    ),
  create: (data: any) => fetchApi<{ success: boolean; data: any; message: string }>('/vouchers', { method: 'POST', body: JSON.stringify(data) }),
  getPendingRefs: (customerId: number, direction?: 'Dr' | 'Cr', excludeVchId?: number) =>
    fetchApi<{ success: boolean; data: { billname: string; amount: number; vch_date: string; vch_no: string; direction: string }[] }>(`/vouchers/pending-refs?customer_id=${customerId}${direction ? `&direction=${direction}` : ''}${excludeVchId ? `&exclude_vch_id=${excludeVchId}` : ''}`),
  getSerials: (customerId: number, flavourId?: number) =>
    fetchApi<{ success: boolean; data: string[] }>(`/vouchers/serials?customer_id=${customerId}${flavourId ? `&flavour_id=${flavourId}` : ''}`),
  getAll: (params: { page?: number; limit?: number; vch_type?: string; search?: string; date_from?: string; date_to?: string }) => {
    const q = new URLSearchParams();
    if (params.page) q.append('page', String(params.page));
    if (params.limit) q.append('limit', String(params.limit));
    if (params.vch_type) q.append('vch_type', params.vch_type);
    if (params.search) q.append('search', params.search);
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: any[]; total: number }>(`/vouchers?${q.toString()}`);
  },
  getTallyPendingReport: (params: { page?: number; limit?: number; vch_type?: string; search?: string; date_from?: string; date_to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.page) q.append('page', String(params.page));
    if (params.limit) q.append('limit', String(params.limit));
    if (params.vch_type) q.append('vch_type', params.vch_type);
    if (params.search) q.append('search', params.search);
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    return fetchApi<TallyPendingTransactionReport>(`/vouchers/tally-pending-report?${q.toString()}`);
  },
  getById: (id: number) => fetchApi<{ success: boolean; data: any }>(`/vouchers/${id}`),
  getDaybook: (params: string | { date?: string; date_from?: string; date_to?: string; vch_type_id?: number }) => {
    // Backwards-compatible: a string still means single-date.
    if (typeof params === 'string') {
      return fetchApi<{ success: boolean; data: any[] }>(`/vouchers/daybook?date=${params}`);
    }
    const q = new URLSearchParams();
    if (params.date)      q.append('date', params.date);
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.vch_type_id) q.append('vch_type_id', String(params.vch_type_id));
    return fetchApi<{ success: boolean; data: any[] }>(`/vouchers/daybook?${q.toString()}`);
  },
  getLedger: (params: { ledger_id: number; date_from?: string; date_to?: string; search?: string }) => {
    const q = new URLSearchParams();
    q.append('ledger_id', String(params.ledger_id));
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{
      success: boolean;
      data: {
        ledger: { id: number; company: string } | null;
        opening: number;
        closing: number;
        totalDebit: number;
        totalCredit: number;
        rows: any[];
      };
    }>(`/vouchers/ledger?${q.toString()}`);
  },
  getLedgerBillWise: (params: { ledger_id: number; as_of?: string }) => {
    const q = new URLSearchParams();
    q.append('ledger_id', String(params.ledger_id));
    if (params.as_of) q.append('as_of', params.as_of);
    return fetchApi<{ success: boolean; data: {
      ledger_id: number; ledger_name: string | null; asOf: string | null;
      bills: Array<{ bill_name: string; bill_date: string | null; overdue_days: number | null; opening_amount: number; pending_amount: number; lines: Array<{ date: string | null; type: string; vch_no: string | null; vch_id: number | null; amount: number; drcr: string }> }>;
      totals: { opening: number; pending: number };
      on_account: { amount: number; drcr: string; date: string | null } | null;
      grand_total: { opening: number; pending: number };
    } }>(`/vouchers/ledger-billwise?${q.toString()}`);
  },
  getSalesRegister: (params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { rows: any[]; totals: any } }>(`/vouchers/sales-register?${q.toString()}`);
  },
  getSalesRegisterMonthly: (params: { date_from?: string; date_to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: { rows: any[]; totals: any } }>(`/vouchers/sales-register/monthly?${q.toString()}`);
  },
  // Item Category-wise × Month-wise Sales Value. `rows[].months` is dense and
  // positionally aligned with `months` / `totals.months` — index i is the same
  // calendar month in all three, so the grid renders without key lookups.
  getCategorySalesMonthly: (params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: {
      months: Array<{ year: number; month: number }>;
      rows: Array<{
        category_id: number; category_name: string;
        months: Array<{ value: number; qty: number; vch_count: number }>;
        total: number; total_qty: number;
      }>;
      totals: { months: Array<{ value: number; qty: number }>; total: number; total_qty: number };
    } }>(`/vouchers/category-sales/monthly?${q.toString()}`);
  },
  getCategorySalesItems: (categoryId: number, params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: {
      category: { id: number; name: string };
      rows: Array<{ item_id: number | null; item_name: string; qty: number; vch_count: number; sales_value: number }>;
      totals: { qty: number; vch_count: number; sales_value: number };
    } }>(`/vouchers/category-sales/${categoryId}/items?${q.toString()}`);
  },
  getGroupSummary: (params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { rows: any[]; totals: any; profit: GroupSummaryProfit | null; balance_sheet: GroupSummaryBalanceSheet | null } }>(`/vouchers/group-summary?${q.toString()}`);
  },
  // Standalone Balance Sheet report (Tally two-column format).
  getBalanceSheet: (params: { date_from?: string; date_to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: BalanceSheetReport }>(`/vouchers/balance-sheet?${q.toString()}`);
  },
  // Standalone Profit & Loss A/c report (Tally T-format).
  getProfitLoss: (params: { date_from?: string; date_to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: {
      profit: GroupSummaryProfit | null;
      brought_forward: number;
      date_from: string | null;
      date_to: string | null;
    } }>(`/vouchers/profit-loss?${q.toString()}`);
  },
  // "Ledger-wise" at the root: every ledger in the company, flat, each row
  // carrying the group it belongs to.
  getAllLedgersFlat: (params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { group: { id: number; name: string }; rows: any[]; totals: any; balance_sheet: GroupSummaryBalanceSheet | null } }>(`/vouchers/group-summary/all-ledgers?${q.toString()}`);
  },
  getGroupLedgers: (groupId: number, params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { group: { id: number; name: string }; rows: any[]; totals: any } }>(`/vouchers/group-summary/${groupId}/ledgers?${q.toString()}`);
  },
  // "Ledger-wise" view: every ledger under a group + all its sub-groups, flat.
  getGroupLedgersFlat: (groupId: number, params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { group: { id: number; name: string }; rows: any[]; totals: any } }>(`/vouchers/group-summary/${groupId}/ledgers-flat?${q.toString()}`);
  },
  getUserWiseOutstanding: (params: { as_of?: string; search?: string; group_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.as_of) q.append('as_of', params.as_of);
    if (params.search) q.append('search', params.search);
    if (params.group_id) q.append('group_id', String(params.group_id));
    return fetchApi<{ success: boolean; data: { rows: any[]; totals: any; asOf: string | null; group: { id: number; name: string } | null } }>(`/vouchers/user-wise-outstanding?${q.toString()}`);
  },
  getPartyBillItemReport: (params: { date_from?: string; date_to?: string; search?: string; group_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    if (params.search) q.append('search', params.search);
    if (params.group_id) q.append('group_id', String(params.group_id));
    return fetchApi<{ success: boolean; data: { rows: Array<{ party_id: number; party_name: string; ledger_group_id: number | null; ledger_group_name: string | null; vch_id: number; vch_no: string | null; vch_date: string; bill_name: string | null; item_id: number | null; item_name: string; qty: number; rate: number; amount: number; gst_amount: number; credit_note: number | null; received: number | null; comm_type: 'qty' | 'value' | 'cloud' | null; comm_rate: number | null; comm_amount: number | null; net_payable: number | null; commission_vch_no: string | null; commission_amount: number | null }>; totals: { qty: number; amount: number; gst: number; commission: number; credit_note: number; received: number; net_payable: number; commission_posted: number; bills: number; parties: number } } }>(`/vouchers/party-bill-item-report?${q.toString()}`);
  },
  getBatchTransactions: (params: { item_id: number; batch_name: string; date_to?: string }) => {
    const q = new URLSearchParams();
    q.append('item_id', String(params.item_id));
    q.append('batch_name', params.batch_name);
    if (params.date_to) q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: { item_name: string | null; batch_name: string; closing_qty: number; rows: Array<{ vch_id: number; vch_no: string | null; vch_date: string; family: string; vch_type: string; party_id: number | null; party_name: string | null; item_name: string; direction: 'in' | 'out' | 'none'; qty: number; rate: number; amount: number; balance_qty: number }> } }>(`/vouchers/batch-transactions?${q.toString()}`);
  },
  getBatchWiseSummary: (params: { date_from?: string; date_to?: string; search?: string; item_id?: number; item_group_id?: number; category_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    if (params.search) q.append('search', params.search);
    if (params.item_id) q.append('item_id', String(params.item_id));
    if (params.item_group_id) q.append('item_group_id', String(params.item_group_id));
    if (params.category_id) q.append('category_id', String(params.category_id));
    return fetchApi<{ success: boolean; data: { rows: Array<{ item_id: number; item_name: string; batch_name: string; opening_qty: number; opening_amount: number; purchase_qty: number; purchase_amount: number; sales_qty: number; sales_amount: number; credit_note_qty: number; credit_note_amount: number; debit_note_qty: number; debit_note_amount: number; stock_journal_qty: number; stock_journal_amount: number; closing_qty: number; closing_amount: number; commission_amount: number; purchase_date: string | null; sales_date: string | null }>; totals: { opening_qty: number; opening_amount: number; purchase_qty: number; purchase_amount: number; sales_qty: number; sales_amount: number; credit_note_qty: number; credit_note_amount: number; debit_note_qty: number; debit_note_amount: number; stock_journal_qty: number; stock_journal_amount: number; closing_qty: number; closing_amount: number; commission_amount: number; items: number; batches: number } } }>(`/vouchers/batch-wise-summary?${q.toString()}`);
  },
  createCommissionEntry: (body: { vch_date?: string; bills: Array<{ vch_id: number; vch_no: string | null; group_id: number | null; group_name: string | null; net_payable: number }> }) =>
    fetchApi<{ success: boolean; message: string; data: { created: Array<{ group: string; vch_no: string; id: number; bills: number; amount: number }>; skipped: Array<{ group: string; reason: string }> } }>(`/vouchers/commission-entry`, { method: 'POST', body: JSON.stringify(body) }),
  getMonthWiseOutstanding: (params: { as_of?: string; search?: string; side?: 'receivable' | 'payable' | 'all'; group_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.as_of) q.append('as_of', params.as_of);
    if (params.search) q.append('search', params.search);
    if (params.side) q.append('side', params.side);
    if (params.group_id) q.append('group_id', String(params.group_id));
    return fetchApi<{ success: boolean; data: { rows: Array<{ month: string | null; month_label: string; bill_count: number; party_count: number; receivable: number; payable: number; net: number }>; totals: { bill_count: number; party_count: number; receivable: number; payable: number; net: number }; asOf: string | null } }>(`/vouchers/month-wise-outstanding?${q.toString()}`);
  },
  getUserMonthOutstanding: (params: { as_of?: string; search?: string; side?: 'receivable' | 'payable' | 'all'; group_id?: number | null } = {}) => {
    const q = new URLSearchParams();
    if (params.as_of) q.append('as_of', params.as_of);
    if (params.search) q.append('search', params.search);
    if (params.side) q.append('side', params.side);
    if (params.group_id) q.append('group_id', String(params.group_id));
    return fetchApi<{ success: boolean; data: {
      months: Array<{ key: string; label: string }>;
      rows: Array<{ type: 'group' | 'ledger'; id: number; name: string; cells: Record<string, number>; total: number; bill_count: number }>;
      totals: { cells: Record<string, number>; total: number; bill_count: number };
      group: { id: number; name: string } | null;
      path: Array<{ id: number; name: string }>;
      asOf: string | null;
    } }>(`/vouchers/month-wise-outstanding/user-wise?${q.toString()}`);
  },
  getMonthOutstandingDetail: (params: { month: string; as_of?: string; search?: string; side?: 'receivable' | 'payable' | 'all' }) => {
    const q = new URLSearchParams();
    q.append('month', params.month || 'none');
    if (params.as_of) q.append('as_of', params.as_of);
    if (params.search) q.append('search', params.search);
    if (params.side) q.append('side', params.side);
    return fetchApi<{ success: boolean; data: { month: string; rows: Array<{ ledger_id: number; party_name: string; bill_name: string; bill_date: string | null; amount: number; drcr: string }>; totals: { bill_count: number; receivable: number; payable: number; net: number } } }>(`/vouchers/month-wise-outstanding/detail?${q.toString()}`);
  },
  getStockSummary: (params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { rows: any[]; totals: any } }>(`/vouchers/stock-summary?${q.toString()}`);
  },
  // Statistics report — voucher-type counts + account-type counts.
  getStatistics: (params: { date_from?: string; date_to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    const query = q.toString();
    return fetchApi<{ success: boolean; data: {
    voucherTypes: { id: number; name: string; count: number }[];
    voucherTotal: number;
    accounts: { label: string; count: number }[];
    } }>(`/vouchers/statistics${query ? `?${query}` : ''}`);
  },
  getStatisticsMonthly: (vchTypeId: number) => fetchApi<{ success: boolean; data: {
    voucherType: { id: number; name: string };
    financialYear: number;
    months: { label: string; year: number; month: number; count: number; dateFrom: string; dateTo: string }[];
  } }>(`/vouchers/statistics/monthly?vch_type_id=${vchTypeId}`),
  getStockGroupSummary: (params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { rows: any[]; totals: any } }>(`/vouchers/stock-summary/groups?${q.toString()}`);
  },
  getStockGroupItems: (groupId: number, params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { group: { id: number; name: string }; rows: any[]; totals: any } }>(`/vouchers/stock-summary/groups/${groupId}/items?${q.toString()}`);
  },
  getStockCategorySummary: (params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { rows: any[]; totals: any } }>(`/vouchers/stock-summary/categories?${q.toString()}`);
  },
  getStockCategoryItems: (categoryId: number, params: { date_from?: string; date_to?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { group: { id: number; name: string }; rows: any[]; totals: any } }>(`/vouchers/stock-summary/categories/${categoryId}/items?${q.toString()}`);
  },
  // Stock Summary — batch-wise breakdown for one item. Separate, item-scoped
  // action alongside the item transaction ledger (Stock Summary report only).
  getItemBatchSummary: (itemId: number, params: { date_from?: string; date_to?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: { item_name: string | null; rows: any[]; totals: any } }>(`/vouchers/stock-summary/items/${itemId}/batches?${q.toString()}`);
  },
  // Stock Summary — batch-wise drill-down level 2: voucher register for one batch.
  getItemBatchVoucherRegister: (itemId: number, params: { batch_name: string; date_from?: string; date_to?: string }) => {
    const q = new URLSearchParams();
    q.append('batch_name', params.batch_name);
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: {
      item: { id: number; name: string } | null; batch_name: string;
      opening: { qty: number; value: number }; closing: { qty: number; value: number }; rows: any[];
    } }>(`/vouchers/stock-summary/items/${itemId}/batch-register?${q.toString()}`);
  },
  getStockItemLedger: (params: { item_id: number; date_from?: string; date_to?: string; search?: string }) => {
    const q = new URLSearchParams();
    q.append('item_id', String(params.item_id));
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.search)    q.append('search', params.search);
    return fetchApi<{
      success: boolean;
      data: {
        item: { id: number; name: string } | null;
        opening: { qty: number; value: number };
        closing: { qty: number; value: number };
        rows: any[];
      };
    }>(`/vouchers/stock-item-ledger?${q.toString()}`);
  },
  getOutstanding: (params: {
    as_of?: string;
    date_from?: string;
    date_to?: string;
    bill_name?: string;
    bill_month?: string;
    search?: string;
    side?: 'receivable' | 'payable' | 'all';
    group_id?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.as_of)     q.append('as_of', params.as_of);
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to)   q.append('date_to', params.date_to);
    if (params.bill_name) q.append('bill_name', params.bill_name);
    if (params.bill_month) q.append('bill_month', params.bill_month);
    if (params.search)    q.append('search', params.search);
    if (params.side)      q.append('side', params.side);
    if (params.group_id)  q.append('group_id', String(params.group_id));
    return fetchApi<{ success: boolean; data: { bills: any[]; totalReceivable: number; totalPayable: number; asOf: string | null } }>(`/vouchers/outstanding?${q.toString()}`);
  },
  getLedgerGroupTree: () =>
    fetchApi<{ success: boolean; data: Array<{ id: number; name: string; parent_id: number | null }> }>('/vouchers/ledger-group-tree'),
  upsertBillFollowup: (data: {
    ledger_id: number; bill_name: string; status?: string; person_name?: string; phone_number?: string; next_date?: string; remark?: string;
  }) => fetchApi<{ success: boolean }>('/vouchers/bill-followup', { method: 'POST', body: JSON.stringify(data) }),
  getBillFollowupHistory: (ledgerId: number, billName: string) =>
    fetchApi<{ success: boolean; data: any[] }>(`/vouchers/bill-followup/history?ledger_id=${ledgerId}&bill_name=${encodeURIComponent(billName)}`),
  getBillTransactions: (ledgerId: number, billName: string, asOf?: string) =>
    fetchApi<{ success: boolean; data: { bill_name: string; party_name: string | null; closing: number; as_of: string | null; excluded_count: number; transactions: Array<{ id: number; vch_id: number | null; vch_no: string | null; vch_type: string | null; vch_type_id: number | null; vch_date: string | null; is_opening?: boolean; beyond_as_of?: boolean; debit: number; credit: number; running_balance: number }> } }>(`/vouchers/bill-transactions?ledger_id=${ledgerId}&bill_name=${encodeURIComponent(billName)}${asOf ? `&as_of=${encodeURIComponent(asOf)}` : ''}`),
  getOnAccount: (params: { as_of?: string; search?: string; group_id?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.as_of) q.append('as_of', params.as_of);
    if (params.search) q.append('search', params.search);
    if (params.group_id) q.append('group_id', String(params.group_id));
    return fetchApi<{ success: boolean; data: { rows: Array<{ ledger_id: number; party_name: string; entry_count: number; net_amount: number; opening_amount: number; total_amount: number; last_date: string | null }>; totals: { entry_count: number; net_dr: number; net_cr: number; opening: number }; asOf: string | null } }>(`/vouchers/on-account?${q.toString()}`);
  },
  getOnAccountDetail: (ledgerId: number, asOf?: string) => {
    const q = new URLSearchParams({ ledger_id: String(ledgerId) });
    if (asOf) q.append('as_of', asOf);
    return fetchApi<{ success: boolean; data: { party_name: string | null; closing: number; transactions: Array<{ id: number; vch_id: number | null; vch_no: string | null; vch_type: string | null; vch_date: string | null; is_opening?: boolean; debit: number; credit: number; running_balance: number }> } }>(`/vouchers/on-account/detail?${q.toString()}`);
  },
  deleteVoucher: (id: number) => fetchApi<{ success: boolean }>(`/vouchers/${id}`, { method: 'DELETE' }),
  update: (id: number, data: any) => fetchApi<{ success: boolean; data: any; message: string }>(`/vouchers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getNextNo: (vchTypeId: number, forDate?: string) => fetchApi<{ success: boolean; data: string }>(`/vouchers/next-no?vch_type_id=${vchTypeId}${forDate ? `&for_date=${forDate}` : ''}`),
  markChecked:   (id: number) => fetchApi<{ success: boolean; message: string }>(`/vouchers/${id}/check`,   { method: 'POST' }),
  markUnchecked: (id: number) => fetchApi<{ success: boolean; message: string }>(`/vouchers/${id}/uncheck`, { method: 'POST' }),
  markCheckedBulk: (ids: number[]) => fetchApi<{ success: boolean; updated: number; message: string }>(`/vouchers/check-bulk`, { method: 'POST', body: JSON.stringify({ ids }) }),
  markCheckedAll:  (params: { date_from?: string; date_to?: string } = {}) => fetchApi<{ success: boolean; updated: number; message: string }>(`/vouchers/check-all`, { method: 'POST', body: JSON.stringify(params) }),
  retrofitNumbering: (params: {
    vch_type_id: number; old_prefix: string; old_suffix: string; new_prefix: string; new_suffix: string;
    from_date: string; to_date?: string; renumber_existing?: boolean; start_no?: number; width?: number; dry_run: boolean;
  }) => fetchApi<{ success: boolean; data: { changed: { id: number; old: string; new: string }[]; skipped: { id: number; vch_no: string }[] } }>(
    '/vouchers/retrofit-numbering', { method: 'POST', body: JSON.stringify(params) },
  ),
  // ── Share (Email / WhatsApp / SMS) ──
  uploadSharePdf: async (id: number, pdfBlob: Blob) => {
    const fd = new FormData();
    fd.append('file', pdfBlob, `voucher-${id}.pdf`);
    const token = getToken();
    const res = await fetch(`${API_BASE}/vouchers/${id}/share-pdf`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    const json = await res.json();
    if (!res.ok || !json.success) throw new Error(json.message || 'Failed to upload voucher PDF');
    return json as { success: boolean; data: { token: string; public_path: string } };
  },
  shareVoucherEmail: (id: number, data: { token: string; to?: string }) =>
    fetchApi<{ success: boolean; sent_to: string }>(`/vouchers/${id}/share-email`, { method: 'POST', body: JSON.stringify(data) }),
  getShareSummary: (id: number) =>
    fetchApi<{ success: boolean; data: { vch_no: string | null; vch_date: string | null; vch_type: string; amount: number; party_name: string | null; party_email: string | null; party_mobile: string | null; contact_person: string | null } }>(`/vouchers/${id}/share-summary`),
};

/** Absolute backend origin for building public (shareable) links. */
export const getApiOrigin = () => API_BASE.replace(/\/api\/?$/, '') || window.location.origin;

export const attendanceApi = {
  checkIn: (lat: number, lng: number) => fetchApi<{ success: boolean; message: string; office: string }>('/attendance/checkin', {
    method: 'POST',
    body: JSON.stringify({ lat, lng }),
  }),
  checkOut: (lat: number, lng: number) => fetchApi<{ success: boolean; message: string }>('/attendance/checkout', {
    method: 'POST',
    body: JSON.stringify({ lat, lng }),
  }),
  getStatus: () => fetchApi<{ status: string; checkin?: string; checkout?: string }>('/attendance/status'),
  getDailyReport: (date: string) => fetchApi<any[]>(`/attendance/report?date=${date}`),
  getUserHistory: (userId: string, from: string, to: string) => fetchApi<any[]>(`/attendance/history/${userId}?from=${from}&to=${to}`),
  forceCheckIn: (data: any) => fetchApi<{ success: boolean; message: string }>('/attendance/force-checkin', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  forceCheckOut: (data: any) => fetchApi<{ success: boolean; message: string }>('/attendance/force-checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getHolidays: () => fetchApi<any[]>('/attendance/holidays'),
  addHoliday: (date: string, description: string) => fetchApi<{ success: boolean; message: string }>('/attendance/holidays', {
    method: 'POST',
    body: JSON.stringify({ date, description }),
  }),
  bulkAddHolidays: (holidays: { date: string; description: string }[]) => fetchApi<{ success: boolean; message: string }>('/attendance/holidays/bulk', {
    method: 'POST',
    body: JSON.stringify({ holidays }),
  }),
  removeHoliday: (date: string) => fetchApi<{ success: boolean; message: string }>('/attendance/holidays/remove', {
    method: 'POST',
    body: JSON.stringify({ date }),
  }),
  getMyMonthlyStats: (month: number, year: number) => fetchApi<any>(`/attendance/my-monthly-stats?month=${month}&year=${year}`),
  getMonthlyExport: (month: number, year: number) => fetchApi<any>(`/attendance/monthly-export?month=${month}&year=${year}`),
};

export const tallyApi = {
  getExpiryReport: (params: {
    customer_type?: 'our' | 'not_our';
    expiry_status?: string;
    search?: string;
    page?: number;
    limit?: number;
    date_from?: string;
    date_to?: string;
    pincode?: string;
    group_id?: number;
    subgroup_id?: number;
  }) => {
    const q = new URLSearchParams();
    if (params.customer_type) q.append('customer_type', params.customer_type);
    if (params.expiry_status) q.append('expiry_status', params.expiry_status);
    if (params.search) q.append('search', params.search);
    if (params.page) q.append('page', params.page.toString());
    if (params.limit) q.append('limit', params.limit.toString());
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    if (params.pincode) q.append('pincode', params.pincode);
    if (params.group_id) q.append('group_id', params.group_id.toString());
    if (params.subgroup_id) q.append('subgroup_id', params.subgroup_id.toString());
    return fetchApi<{
      data: any[];
      total: number;
      allCount: number;
      statusCounts: Record<string, number>;
      page: number;
      limit: number;
    }>(`/tally/expiry-report?${q.toString()}`);

  },
  getLedgerGroups: () => fetchApi<{ success: boolean; data: Array<{ id: number; name: string; parent_id: number | null }> }>('/tally/ledger-groups'),
  getPincodeSummary: (params: { search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.append('search', params.search);
    return fetchApi<{
      rows: Array<{ pincode: string; city: string; state: string; our_count: number; not_our_count: number; total: number }>;
      totals: { our_count: number; not_our_count: number; total: number; pincodes: number };
    }>(`/tally/pincode-summary?${q.toString()}`);
  },
  updateRenewalCall: (data: any) => fetchApi<{ success: boolean }>('/tally/renewal-call', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  upsertDetail: (data: any) => fetchApi<{ success: boolean }>('/tally/upsert-detail', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  syncSerial: (serial: string) => fetchApi<{ success: boolean; data?: any; message?: string }>('/tally/sync-serial', {
    method: 'POST',
    body: JSON.stringify({ serial }),
  }),
  markBilled: (data: { tallyserial: string; voucher_id: number }) => fetchApi<{ success: boolean; updated?: number; message?: string }>('/tally/mark-billed', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getExpiryCallHistory: (serial: string) =>
    fetchApi<{ success: boolean; data: any[] }>(`/tally/expiry-call-history?serial=${encodeURIComponent(serial)}`),
};

const api = {
  auth: authApi,
  servers: serversApi,
  customers: customersApi,
  mappings: mappingsApi,
  activities: activitiesApi,
  users: usersApi,
  dashboard: dashboardApi,
  tdl: tdlApi,
  attendance: attendanceApi,
  // methods directly on default export for backward compat if any? 
  get: (url: string) => fetchApi(url),
  post: (url: string, data: any) => fetchApi(url, { method: 'POST', body: JSON.stringify(data) }),
  put: (url: string, data: any) => fetchApi(url, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (url: string) => fetchApi(url, { method: 'DELETE' }),
};


// ── Complains API ──
export const complainsApi = {
  getAll: (params: { status?: string; search?: string; staff?: string; date_from?: string; date_to?: string; page?: number; limit?: number } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.append(k, String(v)); });
    return fetchApi<any>(`/complains?${q.toString()}`);
  },
  getOne: (id: number) => fetchApi<any>(`/complains/${id}`),
  create: (data: { phone_no: string; remark: string }) =>
    fetchApi<any>('/complains', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: { phone_no?: string; remark?: string }) =>
    fetchApi<any>(`/complains/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  close: (id: number, data: { customer_name?: string; contact_person?: string; closing_remark?: string; status: string; customer_id?: number | null }) =>
    fetchApi<any>(`/complains/${id}/close`, { method: 'PUT', body: JSON.stringify(data) }),
  reopen: (id: number) => fetchApi<any>(`/complains/${id}/reopen`, { method: 'PUT' }),
  remove: (id: number) => fetchApi<any>(`/complains/${id}`, { method: 'DELETE' }),
};

// ── TDL Requirements API ──
export const tdlRequirementsApi = {
  getProjectDetail: (id: number) =>
    fetchApi<any>(`/tdl-requirements/project/${id}`),
  getRequirements: (projectId: number, status?: string) =>
    fetchApi<any>(`/tdl-requirements/project/${projectId}/requirements${status ? `?status=${status}` : ''}`),
  addRequirement: (projectId: number, data: { description: string; assigned_to?: string; priority?: string; deadline?: string; amount?: number }) =>
    fetchApi<any>(`/tdl-requirements/project/${projectId}/requirements`, { method: 'POST', body: JSON.stringify(data) }),
  updateRequirement: (reqId: number, data: { status?: string; stage?: string; remark?: string; next_followup_date?: string }) =>
    fetchApi<any>(`/tdl-requirements/requirements/${reqId}`, { method: 'PUT', body: JSON.stringify(data) }),
  completeRequirement: (reqId: number, data: { remark?: string; action: string; transfer_to?: string }) =>
    fetchApi<any>(`/tdl-requirements/requirements/${reqId}/complete`, { method: 'PUT', body: JSON.stringify(data) }),
  transferRequirement: (reqId: number, data: { transfer_to: string; remark?: string }) =>
    fetchApi<any>(`/tdl-requirements/requirements/${reqId}/transfer`, { method: 'PUT', body: JSON.stringify(data) }),
  updateRequirementStatus: (reqId: number, status: string) =>
    fetchApi<any>(`/tdl-requirements/requirements/${reqId}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  getRequirementUpdates: (reqId: number) =>
    fetchApi<any>(`/tdl-requirements/requirements/${reqId}/updates`),
  deleteRequirement: (reqId: number) =>
    fetchApi<any>(`/tdl-requirements/requirements/${reqId}`, { method: 'DELETE' }),
  getFollowups: (projectId: number) =>
    fetchApi<any>(`/tdl-requirements/project/${projectId}/followups`),
  addFollowup: (projectId: number, data: { content: string; followup_date?: string }) =>
    fetchApi<any>(`/tdl-requirements/project/${projectId}/followups`, { method: 'POST', body: JSON.stringify(data) }),
  markFollowupDone: (followupId: number) =>
    fetchApi<any>(`/tdl-requirements/followups/${followupId}/done`, { method: 'PUT' }),
  getMyRequirements: () =>
    fetchApi<any>('/tdl-requirements/my-requirements'),
  getRequirementsReport: (params: { search?: string; staff?: string; status?: string; priority?: string; startDate?: string; endDate?: string; page?: number; limit?: number; sortBy?: string; sortOrder?: string }) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.append(k, String(v)); });
    return fetchApi<any>(`/tdl-requirements/report/requirements?${q.toString()}`);
  },
  getCorrectionReport: (params: { search?: string; staff?: string; updateType?: string; startDate?: string; endDate?: string; page?: number; limit?: number }) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') q.append(k, String(v)); });
    return fetchApi<any>(`/tdl-requirements/report/corrections?${q.toString()}`);
  },
};

// Targets API
export const targetsApi = {
  get: (fy?: string, user?: string) => {
    const q = new URLSearchParams();
    if (fy) q.set('fy', fy);
    if (user) q.set('user', user);
    return fetchApi<any>(`/targets?${q.toString()}`);
  },
  save: (fy: string, rows: any[]) =>
    fetchApi<any>('/targets/save', { method: 'POST', body: JSON.stringify({ fy, rows }) }),
  adminCreate: (user_name: string, fy: string, rows: any[]) =>
    fetchApi<any>('/targets/admin', { method: 'POST', body: JSON.stringify({ user_name, fy, rows }) }),
  adminUpdate: (id: number, data: any) =>
    fetchApi<any>(`/targets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  approveAll: (user_name: string, fy: string) =>
    fetchApi<any>('/targets/approve-all', { method: 'POST', body: JSON.stringify({ user_name, fy }) }),
  delete: (id: number) =>
    fetchApi<any>(`/targets/${id}`, { method: 'DELETE' }),
  pendingCount: () =>
    fetchApi<any>('/targets/pending-count'),
  getUnitTypes: (fy?: string, user?: string) => {
    const q = new URLSearchParams();
    if (fy) q.set('fy', fy);
    if (user) q.set('user', user);
    return fetchApi<any>(`/targets/unit-types?${q.toString()}`);
  },
  saveUnitTypes: (user_name: string, fy: string, types: Record<string, string>) =>
    fetchApi<any>('/targets/unit-types', { method: 'POST', body: JSON.stringify({ user_name, fy, types }) }),
};

// TDL Expiry API
export const tdlBillingApi = {
  getAll: (page = 1, limit = 25, search = '') =>
    fetchApi<any>(`/tdl-billing?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
  getCustomers: (search = '') =>
    fetchApi<any>(`/tdl-billing/customers?search=${encodeURIComponent(search)}`),
  getTdlsByCustomer: (customerName: string) =>
    fetchApi<any>(`/tdl-billing/tdls/${encodeURIComponent(customerName)}`),
  prepare: (tdlExpiryId: number, cycle: string, startDate?: string) =>
    fetchApi<any>(`/tdl-billing/prepare/${tdlExpiryId}?cycle=${cycle}${startDate ? `&startDate=${startDate}` : ''}`),
  create: (data: any) =>
    fetchApi<any>('/tdl-billing', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    fetchApi<any>(`/tdl-billing/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) =>
    fetchApi<any>(`/tdl-billing/${id}`, { method: 'DELETE' }),
};

export const serverMonitorApi = {
  getAll: (search = '') => fetchApi<any>(`/server-monitor?search=${encodeURIComponent(search)}`),
  getStatuses: () => fetchApi<{ success: boolean; data: Record<string, 'up' | 'down' | 'unknown'> }>('/server-monitor/statuses'),
  getLogs: (ip: string, limit = 50) => fetchApi<any>(`/server-monitor/${encodeURIComponent(ip)}/logs?limit=${limit}`),
  sync: () => fetchApi<any>('/server-monitor/sync', { method: 'POST' }),
  checkNow: () => fetchApi<any>('/server-monitor/check-now', { method: 'POST' }),
  checkSingle: (ip: string) => fetchApi<any>(`/server-monitor/${encodeURIComponent(ip)}/check`, { method: 'POST' }),
  updatePort: (ip: string, port: number) => fetchApi<any>(`/server-monitor/${encodeURIComponent(ip)}/port`, { method: 'PATCH', body: JSON.stringify({ port }) }),
  setActive: (ip: string, isActive: boolean) => fetchApi<any>(`/server-monitor/${encodeURIComponent(ip)}/active`, { method: 'PATCH', body: JSON.stringify({ is_active: isActive }) }),
};

// Field visits raised against GSTIN prospects. Separate list from customer
// visits (cloud_visits) but the same check-in rules.
export const gstinVisitsApi = {
  getActive: (userName?: string) =>
    fetchApi<{ success: boolean; data: { id: number; gstin_id: number; user_name: string; assigned_by: string; status: string; scheduled_date: string; check_in_time?: string; check_in_lat?: string; check_in_lng?: string; check_out_time?: string; check_out_lat?: string; check_out_lng?: string; check_out_remark?: string; outcome?: string; remark?: string; force_checkin_allowed?: number; customer_name?: string; person_name?: string; mobile_no?: string; address?: string; state?: string; pincode?: string; gstin_no?: string; gstin_lat?: string; gstin_lng?: string }[] }>(`/gstin-visits/active?user_name=${encodeURIComponent(userName || '')}`),
  getCompleted: (params: { page?: number; limit?: number; search?: string; user_name?: string; date_from?: string; date_to?: string } = {}) => {
    const q = new URLSearchParams();
    q.append('page', String(params.page || 1));
    q.append('limit', String(params.limit || 20));
    if (params.search) q.append('search', params.search);
    if (params.user_name) q.append('user_name', params.user_name);
    if (params.date_from) q.append('date_from', params.date_from);
    if (params.date_to) q.append('date_to', params.date_to);
    return fetchApi<{ success: boolean; data: { id: number; gstin_id: number; user_name: string; assigned_by: string; status: string; scheduled_date: string; check_in_time?: string; check_in_lat?: string; check_in_lng?: string; check_out_time?: string; check_out_lat?: string; check_out_lng?: string; check_out_remark?: string; outcome?: string; remark?: string; force_checkin_allowed?: number; customer_name?: string; person_name?: string; mobile_no?: string; address?: string; state?: string; pincode?: string; gstin_no?: string; gstin_lat?: string; gstin_lng?: string }[]; total: number; page: number; limit: number }>(`/gstin-visits/completed?${q.toString()}`);
  },
  getByGstin: (gstinId: number) =>
    fetchApi<{ success: boolean; data: { id: number; gstin_id: number; user_name: string; assigned_by: string; status: string; scheduled_date: string; check_in_time?: string; check_in_lat?: string; check_in_lng?: string; check_out_time?: string; check_out_lat?: string; check_out_lng?: string; check_out_remark?: string; outcome?: string; remark?: string; force_checkin_allowed?: number; customer_name?: string; person_name?: string; mobile_no?: string; address?: string; state?: string; pincode?: string; gstin_no?: string; gstin_lat?: string; gstin_lng?: string }[] }>(`/gstin-visits/by-gstin/${gstinId}`),
  create: (body: { gstin_id: number; user_name?: string; scheduled_date?: string; remark?: string }) =>
    fetchApi<{ success: boolean; id: number }>('/gstin-visits/create', { method: 'POST', body: JSON.stringify(body) }),
  checkIn: (id: number, body: { lat: number; lng: number; accuracy?: number }) =>
    fetchApi<{ success: boolean }>(`/gstin-visits/${id}/check-in`, { method: 'POST', body: JSON.stringify(body) }),
  checkOut: (id: number, body: { lat?: number; lng?: number; accuracy?: number; remark?: string; outcome?: string; details?: { customer_name?: string; person_name?: string; mobile_no?: string; email_id?: string; tally_serial_no?: string; expiry_date?: string; partner_name?: string } }) =>
    fetchApi<{ success: boolean }>(`/gstin-visits/${id}/check-out`, { method: 'POST', body: JSON.stringify(body) }),
  toggleForceCheckin: (id: number, allowed: boolean) =>
    fetchApi<{ success: boolean }>('/gstin-visits/force-checkin', { method: 'POST', body: JSON.stringify({ id, allowed }) }),
  updateDetails: (id: number, details: { customer_name?: string; person_name?: string; mobile_no?: string; email_id?: string; tally_serial_no?: string; expiry_date?: string; partner_name?: string }) =>
    fetchApi<{ success: boolean; updated: number }>(`/gstin-visits/${id}/details`, { method: 'POST', body: JSON.stringify(details) }),
  resetLocation: (id: number) =>
    fetchApi<{ success: boolean; alreadyEmpty?: boolean; customer_name?: string; message?: string }>(`/gstin-visits/${id}/reset-location`, { method: 'POST' }),
  uploadRecording: async (id: number, blob: Blob, ext: string) => {
    const form = new FormData();
    form.append('recording', blob, `gstin-visit-${id}${ext}`);
    const token = getToken();
    const base = window.location.hostname === 'localhost' ? 'http://localhost:5000' : '';
    const r = await fetch(`${base}/api/gstin-visits/${id}/recording`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const text = await r.text();
    let json: any;
    try { json = JSON.parse(text); } catch { json = { success: false, raw: text }; }
    if (!r.ok) throw new Error(`HTTP ${r.status}: ${json?.message || text}`);
    return json as { success: boolean; path?: string };
  },
  getRecordingUrl: (id: number) => `${API_BASE}/gstin-visits/${id}/recording`,
  remove: (id: number) =>
    fetchApi<{ success: boolean }>(`/gstin-visits/${id}`, { method: 'DELETE' }),
};

export const gstinApi = {
  list: (params: { page?: number; limit?: number; search?: string; state?: string; pincode?: string; followup_type?: string; has_tally?: string; has_mobile?: string; followed?: string; owned?: string; existing?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.page) q.append('page', String(params.page));
    if (params.limit) q.append('limit', String(params.limit));
    if (params.search) q.append('search', params.search);
    if (params.state) q.append('state', params.state);
    if (params.pincode) q.append('pincode', params.pincode);
    if (params.followup_type) q.append('followup_type', params.followup_type);
    if (params.has_tally) q.append('has_tally', params.has_tally);
    if (params.has_mobile) q.append('has_mobile', params.has_mobile);
    if (params.followed) q.append('followed', params.followed);
    if (params.owned) q.append('owned', params.owned);
    if (params.existing) q.append('existing', params.existing);
    return fetchApi<{ success: boolean; data: any[]; total: number; page: number; limit: number; totalPages: number }>(`/gstin?${q.toString()}`);
  },
  assign: (ids: number[], user_id: string | null) =>
    fetchApi<{ success: boolean; updated: number; message: string }>(`/gstin/assign`, { method: 'POST', body: JSON.stringify({ ids, user_id }) }),
  pick: (id: number) => fetchApi<{ success: boolean; data: any }>(`/gstin/${id}/pick`, { method: 'POST' }),
  unpick: (id: number) => fetchApi<{ success: boolean; data: any }>(`/gstin/${id}/unpick`, { method: 'POST' }),
  states: () => fetchApi<{ success: boolean; data: string[] }>('/gstin/states'),
  getPincodeSummary: (params: { search?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.search) q.append('search', params.search);
    return fetchApi<{ success: boolean; data: { rows: Array<{ pincode: string; city: string; state: string; gstin_count: number }>; totals: { gstin_count: number; pincodes: number } } }>(`/gstin/pincode-summary?${q.toString()}`);
  },
  facets: (params: { existing?: string } = {}) => fetchApi<{ success: boolean; data: { total: number; states: { name: string; count: number }[]; followup_types: Record<string, number>; has_tally: { yes: number; no: number }; has_mobile: { yes: number; no: number }; followed: { yes: number; no: number }; owned: { me: number; free: number }; existing: { existing: number; new: number } } }>(`/gstin/facets${params.existing ? `?existing=${params.existing}` : ''}`),
  stats: (params: { existing?: string } = {}) => fetchApi<{ success: boolean; data: { total: number; with_tally: number; with_mobile: number; followed: number } }>(`/gstin/stats${params.existing ? `?existing=${params.existing}` : ''}`),
  getFollowups: (id: number) => fetchApi<{ success: boolean; data: any[] }>(`/gstin/${id}/followups`),
  updateDetails: (id: number, data: { customer_name?: string; person_name?: string; mobile_no?: string; email_id?: string; tally_serial_no?: string; expiry_date?: string; partner_name?: string }) =>
    fetchApi<{ success: boolean; data: any }>(`/gstin/${id}/details`, { method: 'PATCH', body: JSON.stringify(data) }),
  addFollowup: (id: number, data: { next_date?: string; followup_type?: string; remark?: string }) =>
    fetchApi<{ success: boolean; data: any }>(`/gstin/${id}/followup`, { method: 'POST', body: JSON.stringify(data) }),
  importRows: (rows: any[]) => fetchApi<{ success: boolean; inserted: number; skipped: number; message: string }>(`/gstin/import`, { method: 'POST', body: JSON.stringify({ rows }) }),
  clearAll: () => fetchApi<{ success: boolean; message: string }>(`/gstin/all`, { method: 'DELETE' }),
};

export const tdlExpiryApi = {
  getAll: (page = 1, limit = 25, search = '') =>
    fetchApi<any>(`/tdl-expiry?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`),
  create: (data: any) =>
    fetchApi<any>('/tdl-expiry', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    fetchApi<any>(`/tdl-expiry/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) =>
    fetchApi<any>(`/tdl-expiry/${id}`, { method: 'DELETE' }),
  setActive: (id: number, isActive: boolean) =>
    fetchApi<any>(`/tdl-expiry/${id}/active`, { method: 'PATCH', body: JSON.stringify({ is_active: isActive }) }),
  // Public URL for Tally to call — just returns the URL string, no fetch
  publicCheckUrl: (token: string) => `${API_BASE}/tdl-expiry/check/${token}`,
};

// ── Task Chatbot: assignment-based tasks with a per-task conversation ──
// user_id is a real user id, or 'group:<taskId>' for a group task, which has
// no single counterpart and opens its shared thread directly.
export type ChatPerson = { user_id: string; user_name: string; pending_count: number; total_count: number; unread: number; last_msg: string | null; last_at: string | null; is_group?: boolean; task_id?: number; member_count?: number };
export type ChatTask = { id: number; title: string; description: string | null; status: 'pending' | 'in_progress' | 'awaiting_approval' | 'done' | 'cancelled'; priority: 'low' | 'normal' | 'high'; due_date: string | null; created_by: string; assigned_to: string; created_name: string; assigned_name: string; assignees: Array<{ id: string; name: string }>; is_group: boolean; member_count: number; counterpart_id: string; counterpart_name: string; i_am_creator: boolean; last_msg: string | null; unread: number; created_at: string; updated_at: string };
// read_count / audience describe a group receipt: 'read by N of M others'.
export type ChatMessage = { id: number; sender_id: string; sender_name: string; body: string; created_at: string; mine: boolean; seen: boolean; read_count?: number; audience?: number };
export type PresenceMap = Record<string, { online: boolean; last_seen: string }>;

export const taskChatApi = {
  users: () => fetchApi<{ success: boolean; data: { id: string; name: string }[] }>('/chat-tasks/users'),
  people: () => fetchApi<{ success: boolean; data: ChatPerson[] }>('/chat-tasks/people'),
  unreadCount: () => fetchApi<{ success: boolean; data: { unread: number } }>('/chat-tasks/unread-count'),
  tasks: (params: { with_user?: string; status?: string; direction?: string; search?: string; overdue?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.with_user) q.append('with_user', params.with_user);
    if (params.status) q.append('status', params.status);
    if (params.direction) q.append('direction', params.direction);
    if (params.search) q.append('search', params.search);
    if (params.overdue) q.append('overdue', 'true');
    return fetchApi<{ success: boolean; data: ChatTask[] }>(`/chat-tasks?${q.toString()}`);
  },
  // assigned_to: one id, or several to make it a group task with a shared thread.
  create: (data: { title: string; description?: string; assigned_to: string | string[]; priority?: string; due_date?: string; message?: string }) =>
    fetchApi<{ success: boolean; data: { id: number } }>('/chat-tasks', { method: 'POST', body: JSON.stringify(data) }),
  detail: (id: number) => fetchApi<{ success: boolean; data: ChatTask }>(`/chat-tasks/${id}`),
  update: (id: number, data: { status?: string; priority?: string; due_date?: string; title?: string; description?: string }) =>
    fetchApi<{ success: boolean; data: any }>(`/chat-tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  messages: (id: number) => fetchApi<{ success: boolean; data: ChatMessage[] }>(`/chat-tasks/${id}/messages`),
  send: (id: number, body: string) => fetchApi<{ success: boolean; data: { id: number } }>(`/chat-tasks/${id}/messages`, { method: 'POST', body: JSON.stringify({ body }) }),
  markRead: (id: number) => fetchApi<{ success: boolean }>(`/chat-tasks/${id}/read`, { method: 'POST' }),
  heartbeat: () => fetchApi<{ success: boolean }>('/chat-tasks/heartbeat', { method: 'POST' }),
  presence: () => fetchApi<{ success: boolean; data: PresenceMap }>('/chat-tasks/presence'),
};

// ── Document Management System ──
export type DocFolder = { id: number; name: string; parent_id: number | null; created_at: string; subfolder_count: number; file_count: number };
export type DocFile = { id: number; folder_id: number | null; original_name: string; size: number; mime_type: string | null; uploaded_by: string | null; created_at: string };
export type DocListing = { folder: { id: number; name: string; parent_id: number | null } | null; breadcrumb: Array<{ id: number; name: string }>; folders: DocFolder[]; files: DocFile[] };
export type DocShare = { token: string; path: string; url: string; expires_at: string | null; file_name: string };

export const documentsApi = {
  list: (parentId?: number | null) =>
    fetchApi<{ success: boolean; data: DocListing }>(`/documents${parentId ? `?parent_id=${parentId}` : ''}`),
  createFolder: (name: string, parent_id?: number | null) =>
    fetchApi<{ success: boolean; data: DocFolder }>('/documents/folders', { method: 'POST', body: JSON.stringify({ name, parent_id: parent_id ?? null }) }),
  renameFolder: (id: number, name: string) =>
    fetchApi<{ success: boolean }>(`/documents/folders/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }),
  deleteFolder: (id: number) =>
    fetchApi<{ success: boolean }>(`/documents/folders/${id}`, { method: 'DELETE' }),
  deleteFile: (id: number) =>
    fetchApi<{ success: boolean }>(`/documents/files/${id}`, { method: 'DELETE' }),
  share: (id: number, expires_in_hours: number | null) =>
    fetchApi<{ success: boolean; data: DocShare }>(`/documents/files/${id}/share`, { method: 'POST', body: JSON.stringify({ expires_in_hours }) }),
  shareFolder: (id: number, expires_in_hours: number | null) =>
    fetchApi<{ success: boolean; data: DocShare }>(`/documents/folders/${id}/share`, { method: 'POST', body: JSON.stringify({ expires_in_hours }) }),
  listShares: (id: number) =>
    fetchApi<{ success: boolean; data: Array<{ id: number; token: string; expires_at: string | null; created_at: string; expired: number }> }>(`/documents/files/${id}/shares`),
  revokeShare: (shareId: number) =>
    fetchApi<{ success: boolean }>(`/documents/shares/${shareId}`, { method: 'DELETE' }),
  // Multipart upload (drag & drop) — raw fetch so the browser sets the multipart boundary.
  upload: async (folderId: number | null, files: File[], onProgress?: (pct: number) => void) => {
    const form = new FormData();
    if (folderId) form.append('folder_id', String(folderId));
    for (const f of files) form.append('files', f, f.name);
    const token = getToken();
    return new Promise<{ success: boolean; data?: any; message?: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/documents/upload`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
      xhr.onload = () => {
        let json: any; try { json = JSON.parse(xhr.responseText); } catch { json = { success: false }; }
        if (xhr.status >= 200 && xhr.status < 300) resolve(json);
        else reject(new Error(json?.message || `Upload failed (HTTP ${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(form);
    });
  },
  // Authenticated download → fetch as blob and save (the endpoint needs the Bearer header).
  download: async (id: number, name: string) => {
    const token = getToken();
    const r = await fetch(`${API_BASE}/documents/files/${id}/download`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) throw new Error(`Download failed (HTTP ${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  },
};

export interface VoucherAttachment {
  id: number;
  vch_id: number;
  original_name: string;
  size: number;
  mime_type: string | null;
  note: string | null;
  uploaded_by: string | null;
  created_at: string;
}

/** Supporting documents on a transaction (supplier invoice, e-way bill, POD…).
 *  Bytes are stored server-side and are only reachable with the Bearer token,
 *  so both download and preview go through fetch-as-blob. */
export const voucherAttachmentsApi = {
  list: (vchId: number) =>
    fetchApi<{ success: boolean; data: VoucherAttachment[] }>(`/vouchers/${vchId}/attachments`),
  counts: (ids: number[]) =>
    fetchApi<{ success: boolean; data: Record<string, number> }>(`/vouchers/attachments/counts?ids=${ids.join(',')}`),
  remove: (attId: number) =>
    fetchApi<{ success: boolean; message: string }>(`/vouchers/attachments/${attId}`, { method: 'DELETE' }),

  // Multipart upload — raw XHR so the browser sets the boundary and we get progress.
  upload: async (vchId: number, files: File[], note?: string, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    for (const f of files) form.append('files', f, f.name);
    if (note) form.append('note', note);
    const token = getToken();
    return new Promise<{ success: boolean; data?: any; message?: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/vouchers/${vchId}/attachments`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
      xhr.onload = () => {
        let json: any; try { json = JSON.parse(xhr.responseText); } catch { json = { success: false }; }
        if (xhr.status >= 200 && xhr.status < 300) resolve(json);
        else reject(new Error(json?.message || `Upload failed (HTTP ${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(form);
    });
  },

  download: async (attId: number, name: string) => {
    const token = getToken();
    const r = await fetch(`${API_BASE}/vouchers/attachments/${attId}/download`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) throw new Error(`Download failed (HTTP ${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  },

  /** Opens the file in a new tab (PDFs / images). Returns the object URL so the
   *  caller can revoke it once the tab has loaded. */
  preview: async (attId: number) => {
    const token = getToken();
    const r = await fetch(`${API_BASE}/vouchers/attachments/${attId}/download?inline=1`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) throw new Error(`Preview failed (HTTP ${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

export interface UserDocument {
  id: number;
  user_id: string;
  original_name: string;
  size: number;
  mime_type: string | null;
  note: string | null;
  uploaded_by: string | null;
  created_at: string;
}

/** Documents held on the user master (ID proof, PAN, contracts…). Admin only —
 *  the bytes are server-side and need the Bearer token, so download and preview
 *  both go through fetch-as-blob. */
export const userDocumentsApi = {
  list: (userId: string) =>
    fetchApi<{ success: boolean; data: UserDocument[] }>(`/users/${encodeURIComponent(userId)}/documents`),
  counts: (ids: string[]) =>
    fetchApi<{ success: boolean; data: Record<string, number> }>(`/users/documents/counts?ids=${encodeURIComponent(ids.join(','))}`),
  remove: (docId: number) =>
    fetchApi<{ success: boolean; message: string }>(`/users/documents/${docId}`, { method: 'DELETE' }),

  upload: async (userId: string, files: File[], note?: string, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    for (const f of files) form.append('files', f, f.name);
    if (note) form.append('note', note);
    const token = getToken();
    return new Promise<{ success: boolean; data?: any; message?: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_BASE}/users/${encodeURIComponent(userId)}/documents`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = e => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
      xhr.onload = () => {
        let json: any; try { json = JSON.parse(xhr.responseText); } catch { json = { success: false }; }
        if (xhr.status >= 200 && xhr.status < 300) resolve(json);
        else reject(new Error(json?.message || `Upload failed (HTTP ${xhr.status})`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(form);
    });
  },

  download: async (docId: number, name: string) => {
    const token = getToken();
    const r = await fetch(`${API_BASE}/users/documents/${docId}/download`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) throw new Error(`Download failed (HTTP ${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  },

  preview: async (docId: number) => {
    const token = getToken();
    const r = await fetch(`${API_BASE}/users/documents/${docId}/download?inline=1`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!r.ok) throw new Error(`Preview failed (HTTP ${r.status})`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
};

export default api;
